import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, doc, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { db } from './firebase-config';
import {
  resolveContactsCollectionPath,
  resolveCompanyLiquidationSettingsPath,
  resolveCompanyOfficeSettingsPath,
  resolveRoutesCollectionPath,
  resolveServiceClosuresCollectionPath,
  resolveServiceContractDocPath,
  resolveServiceContractsCollectionPath,
  resolveServiceLocationsCollectionPath,
  resolveServiceDocPath,
  resolveServicesCollectionPath,
  resolveServiceTemplatesCollectionPath,
  resolveDriversCollectionPath,
  resolveTransportsCollectionPath,
} from './core/company/companyPaths';
import ServiceCreateModal from './modules/service/components/ServiceCreateModal';
import ServiceTemplateModal from './modules/service/components/ServiceTemplateModal';
import ServiceContractModesView from './modules/service/components/ServiceContractModesView';
import ServiceClientCommercialView from './modules/service/components/ServiceClientCommercialView';
import ServiceContractEditorModal from './modules/service/components/ServiceContractEditorModal';
import ServiceClosuresView from './modules/service/components/ServiceClosuresView';
import ServiceCalendarView from './modules/service/components/ServiceCalendarView';
import ServiceMarketView from './modules/service/components/ServiceMarketView';
import {
  buildClientDisplayName,
  inferMarginPreset,
  resolveMarginPresetPercent,
  SERVICE_CONTRACT_MODEL_OPTIONS,
  SERVICE_MODE_OPTIONS,
  SERVICE_PRICING_RULE_OPTIONS,
  SERVICE_PRIORITY_OPTIONS,
  SERVICE_TEMPLATE_FREQUENCY_OPTIONS,
  SERVICE_TRANSPORT_OPTIONS,
} from './modules/service/serviceOptions';
import {
  resolveBillingCadenceLabel,
  resolveOutsideContractFlag,
  resolveServiceBillingCadence,
  resolveContractBillingCadence,
  resolveServiceRecognitionBadge,
} from './modules/service/serviceBilling';
import { buildServiceAddressText } from './modules/service/serviceLocationUtils';
import { geocodeAddress, getRouteStatus } from './modules/logistics/components/LogisticsUtils';
import { estimateServiceCommercialMetrics } from './modules/service/servicePricingEstimate';

const Icon = ({ path, className = 'w-5 h-5' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatShortDateTime = (value) => {
  if (!value) return 'Sin fecha';
  if (typeof value?.toDate === 'function') return formatShortDateTime(value.toDate());
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Sin fecha';
  return new Intl.DateTimeFormat('es-AR', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const resolvePrimaryClientContact = (client = null) => {
  const contacts = Array.isArray(client?.contacts) ? client.contacts : [];
  const firstContact = contacts.find((item) => String(item?.name || item?.phone || item?.email || '').trim()) || null;

  return {
    name: String(firstContact?.name || client?.contactName || '').trim(),
    phone: String(firstContact?.phone || client?.phone || '').trim(),
    email: String(firstContact?.email || client?.email || '').trim(),
  };
};

const buildClientAddressFromSnapshot = (snapshot = null) => {
  if (!snapshot?.address) return null;
  return {
    address: String(snapshot?.address || '').trim(),
    apartment: String(snapshot?.apartment || '').trim(),
    locality: String(snapshot?.locality || '').trim(),
    province: String(snapshot?.province || '').trim(),
    partido: String(snapshot?.partido || '').trim(),
    postalCode: String(snapshot?.postalCode || '').trim(),
    latlng: snapshot?.latlng || null,
  };
};

const syncSharedContactDestination = async ({
  companyId,
  companyName,
  snapshot,
}) => {
  if (!companyId || !snapshot?.address) return;

  const contactsPath = resolveContactsCollectionPath(companyId);
  const contactsRef = collection(db, contactsPath);
  const contactName = String(snapshot?.label || snapshot?.address || '').trim();
  if (!contactName) return;

  const q = query(contactsRef, where('name', '==', contactName));
  const querySnapshot = await getDocs(q);

  const addressPayload = buildClientAddressFromSnapshot(snapshot);
  const contactType = String(snapshot?.contactType || 'cliente').trim() || 'cliente';

  if (querySnapshot.empty) {
    await addDoc(contactsRef, {
      companyId,
      companyName: companyName || companyId,
      name: contactName,
      type: contactType,
      addresses: addressPayload ? [addressPayload] : [],
      contacts: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return;
  }

  const existingContactDoc = querySnapshot.docs[0];
  const existingContactData = existingContactDoc.data() || {};
  const existingAddresses = Array.isArray(existingContactData.addresses)
    ? [...existingContactData.addresses]
    : [];

  const existingAddressIndex = existingAddresses.findIndex(
    (addr) =>
      String(addr?.address || '').trim() === String(snapshot?.address || '').trim() &&
      String(addr?.apartment || '').trim() === String(snapshot?.apartment || '').trim()
  );

  if (existingAddressIndex === -1 && addressPayload) {
    existingAddresses.push(addressPayload);
  } else if (existingAddressIndex >= 0 && addressPayload) {
    existingAddresses[existingAddressIndex] = {
      ...existingAddresses[existingAddressIndex],
      ...addressPayload,
    };
  }

  await updateDoc(doc(db, contactsPath, existingContactDoc.id), {
    type: existingContactData.type || contactType,
    addresses: existingAddresses,
    updatedAt: serverTimestamp(),
  });
};

const hasValidLatLng = (latlng = null) =>
  Boolean(
    latlng &&
      typeof latlng.lat === 'number' &&
      Number.isFinite(latlng.lat) &&
      typeof latlng.lng === 'number' &&
      Number.isFinite(latlng.lng)
  );

const normalizeServiceStatus = (value = '') =>
  String(value || 'pending')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');

const SERVICE_STATUS_META = {
  pending: {
    label: 'Pendiente comercial',
    tone: 'border-amber-200 bg-amber-50 text-amber-700',
  },
  pending_logistics: {
    label: 'Pendiente de logística',
    tone: 'border-violet-200 bg-violet-50 text-violet-700',
  },
  assigned: {
    label: 'Tomado por logística',
    tone: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  },
  scheduled: {
    label: 'Programado',
    tone: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  },
  in_progress: {
    label: 'En curso',
    tone: 'border-sky-200 bg-sky-50 text-sky-700',
  },
  completed: {
    label: 'Realizado',
    tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  invoiced: {
    label: 'Facturado',
    tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  paid: {
    label: 'Cobrado',
    tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  cancelled: {
    label: 'Cancelado',
    tone: 'border-slate-200 bg-slate-100 text-slate-600',
  },
};

const getServiceStatusMeta = (value = '') => {
  const normalized = normalizeServiceStatus(value);
  return SERVICE_STATUS_META[normalized] || {
    label: String(value || 'Pendiente').replace(/_/g, ' '),
    tone: 'border-slate-200 bg-slate-100 text-slate-700',
  };
};

const getServiceStatusSurface = (value = '') => {
  const normalized = normalizeServiceStatus(value);
  const surfaceByStatus = {
    pending: 'border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.98)_0%,rgba(255,255,255,1)_65%)]',
    pending_logistics: 'border-violet-200 bg-[linear-gradient(135deg,rgba(245,243,255,0.98)_0%,rgba(255,255,255,1)_65%)]',
    assigned: 'border-cyan-200 bg-[linear-gradient(135deg,rgba(236,254,255,0.98)_0%,rgba(255,255,255,1)_65%)]',
    scheduled: 'border-cyan-200 bg-[linear-gradient(135deg,rgba(236,254,255,0.98)_0%,rgba(255,255,255,1)_65%)]',
    in_progress: 'border-sky-200 bg-[linear-gradient(135deg,rgba(239,246,255,0.98)_0%,rgba(255,255,255,1)_65%)]',
    completed: 'border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.98)_0%,rgba(255,255,255,1)_65%)]',
    invoiced: 'border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.98)_0%,rgba(255,255,255,1)_65%)]',
    paid: 'border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.98)_0%,rgba(255,255,255,1)_65%)]',
    cancelled: 'border-slate-200 bg-[linear-gradient(135deg,rgba(248,250,252,0.98)_0%,rgba(255,255,255,1)_65%)]',
  };

  return surfaceByStatus[normalized] || surfaceByStatus.pending;
};

const ACTIVE_SERVICE_STATUSES = new Set([
  'pending',
  'draft',
  'pending_logistics',
  'assigned',
  'scheduled',
  'in_progress',
  'in-progress',
  'en_curso',
  'en-curso',
]);

const getServiceTimelineTime = (service = {}) => {
  const candidate =
    service?.financialClosedAt ||
    service?.revenueRecognizedAt ||
    service?.completedAt ||
    service?.paidAt ||
    service?.invoicedAt ||
    service?.scheduledDate ||
    service?.date ||
    service?.createdAt;

  const parsed = typeof candidate?.toDate === 'function' ? candidate.toDate() : candidate;
  const timestamp = new Date(parsed || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const TAB_OPTIONS = [
  {
    key: 'services',
    label: 'Servicios',
    caption: 'Pedidos comerciales listos para asignar, ejecutar o cerrar.',
    iconPath: 'M3.75 6.75h16.5M6.75 3.75v6m10.5-6v6M4.5 10.5h15A2.25 2.25 0 0121.75 12.75v5.25A2.25 2.25 0 0119.5 20.25h-15A2.25 2.25 0 012.25 18v-5.25A2.25 2.25 0 014.5 10.5z',
  },
  {
    key: 'scheduled',
    label: 'Programados',
    caption: 'Plantillas recurrentes para generar un servicio con un clic.',
    iconPath: 'M8.25 6.75h7.5M8.25 10.5h7.5m-10.5 7.5h10.5A2.25 2.25 0 0018 15.75V6A2.25 2.25 0 0015.75 3.75H8.25A2.25 2.25 0 006 6v9.75A2.25 2.25 0 008.25 18z',
  },
  {
    key: 'clients',
    label: 'Clientes',
    caption: 'Base comercial con historial, búsqueda y estado de cada cliente.',
    iconPath: 'M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975M15 9.75a3 3 0 11-6 0 3 3 0 016 0zm6 8.25a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    key: 'contracts',
    label: 'Contratos',
    caption: 'Ajustes y acuerdos activos: por viaje, mensual variable y abonos.',
    iconPath: 'M16.5 3.75v16.5l-4.5-2.25-4.5 2.25V3.75m9 0h-9A2.25 2.25 0 005.25 6v12A2.25 2.25 0 007.5 20.25h9A2.25 2.25 0 0018.75 18V6A2.25 2.25 0 0016.5 3.75z',
  },
  {
    key: 'closures',
    label: 'Cierres',
    caption: 'Acumulados semanales y mensuales listos para cobrar, revisar y marcar como pagados.',
    iconPath: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    key: 'calendar',
    label: 'Calendario',
    caption: 'Agenda global de servicios, días pactados, franjas horarias y cobros próximos.',
    iconPath: 'M8.25 6.75h7.5M8.25 10.5h7.5m-10.5 7.5h10.5A2.25 2.25 0 0018 15.75V6A2.25 2.25 0 0015.75 3.75H8.25A2.25 2.25 0 006 6v9.75A2.25 2.25 0 008.25 18z',
  },
  {
    key: 'market',
    label: 'Precios de mercado',
    caption: 'Referencias externas para entender costos, márgenes y ajustes de tarifa.',
    iconPath: 'M3.75 3v18h16.5M7.5 14.25l3-3 2.25 2.25 4.5-4.5M18 8.25h.008v.008H18V8.25z',
  },
];

const SummaryCard = ({ title, value, helper, tone = 'slate' }) => {
  const toneClasses = {
    slate: 'border-slate-200 bg-[linear-gradient(145deg,#ffffff_0%,#f8fafc_100%)] text-slate-900 shadow-[0_10px_24px_rgba(15,23,42,0.05)]',
    emerald: 'border-emerald-200 bg-[linear-gradient(145deg,#ecfdf5_0%,#ffffff_100%)] text-emerald-900 shadow-[0_10px_24px_rgba(16,185,129,0.06)]',
    cyan: 'border-cyan-200 bg-[linear-gradient(145deg,#ecfeff_0%,#ffffff_100%)] text-cyan-900 shadow-[0_10px_24px_rgba(8,145,178,0.06)]',
    violet: 'border-violet-200 bg-[linear-gradient(145deg,#f5f3ff_0%,#ffffff_100%)] text-violet-900 shadow-[0_10px_24px_rgba(124,58,237,0.06)]',
    amber: 'border-amber-200 bg-[linear-gradient(145deg,#fffbeb_0%,#ffffff_100%)] text-amber-900 shadow-[0_10px_24px_rgba(245,158,11,0.06)]',
  };

  return (
    <div className={`relative overflow-hidden rounded-[22px] border px-4 py-3 ${toneClasses[tone] || toneClasses.slate}`}>
      <div className="absolute -right-5 -top-5 h-14 w-14 rounded-full bg-white/50 blur-2xl" />
      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[9px] font-semibold uppercase tracking-[0.22em] opacity-70">{title}</p>
          <p className="mt-1.5 text-[1.7rem] font-black tracking-[-0.05em] leading-none">{value}</p>
        </div>
      </div>
      <p className="relative mt-1.5 text-[11px] leading-4 opacity-75">{helper}</p>
    </div>
  );
};

const EmptyState = ({ id = undefined, title, description, action = null, footer = null }) => (
  <div id={id} className="rounded-[28px] border border-dashed border-slate-300 bg-slate-50/70 px-6 py-10 text-center">
    <p className="text-base font-bold text-slate-900">{title}</p>
    <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
    {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    {footer ? <div className="mt-5">{footer}</div> : null}
  </div>
);

const buildEstimatedPricingSnapshot = (serviceLike = {}, liquidationSettings = {}) => {
  const estimate = estimateServiceCommercialMetrics({
    service: serviceLike,
    liquidationSettings,
  });

  return {
    estimatedTransportType: estimate.transportType || '',
    estimatedDistanceKm: Number(estimate.estimatedDistanceKm || 0) || 0,
    estimatedOperationalCost: Number(estimate.estimatedOperationalCost || 0) || 0,
    estimatedMargin: Number(estimate.estimatedMargin || 0) || 0,
    estimatedTotalToCharge: Number(estimate.estimatedTotalToCharge || 0) || 0,
    monthlyFeeSnapshot: Number(estimate.monthlyFeeSnapshot || 0) || 0,
    hasEstimate: Boolean(estimate.hasEstimate),
  };
};

const resolveCommercialSnapshot = ({
  linkedContract = null,
  form = {},
  forceOutsideContract = false,
}) => {
  const useClientPricing =
    Boolean(form?.useClientPricing) &&
    Boolean(linkedContract) &&
    !forceOutsideContract;
  const commercialModel = useClientPricing
    ? linkedContract?.commercialModel || 'trip_rate'
    : form?.commercialModel || 'trip_rate';
  const pricingRule = commercialModel === 'monthly_fixed'
    ? ''
    : useClientPricing
    ? linkedContract?.pricingRule || 'percentage_margin'
    : form?.pricingRule || 'percentage_margin';

  return {
    commercialModel,
    pricingRule,
    monthlyFeeSnapshot: Number(useClientPricing ? linkedContract?.monthlyFee : form?.monthlyFee) || 0,
    agreedPricePerTripSnapshot: Number(useClientPricing ? linkedContract?.agreedPricePerTrip : form?.agreedPricePerTrip) || 0,
    fixedMarginAmountSnapshot: Number(useClientPricing ? linkedContract?.fixedMarginAmount : form?.fixedMarginAmount) || 0,
    marginPresetSnapshot:
      pricingRule === 'percentage_margin'
        ? useClientPricing
          ? linkedContract?.marginPreset || inferMarginPreset(linkedContract?.marginPercent)
          : form?.marginPreset || inferMarginPreset(form?.marginPercent)
        : '',
    marginPercentSnapshot:
      pricingRule === 'percentage_margin'
        ? useClientPricing
          ? Number(linkedContract?.marginPercent) || resolveMarginPresetPercent(linkedContract?.marginPreset || inferMarginPreset(linkedContract?.marginPercent))
          : (form?.marginPreset === 'manual'
              ? Number(form?.marginPercent) || 0
              : resolveMarginPresetPercent(form?.marginPreset || 'normal'))
        : 0,
    useClientPricing,
  };
};

const ServiceApp = ({
  onBackToMenu,
  onGoToLogistics,
  onStartTutorial,
  tutorialSeen = false,
  tutorialActive = false,
  tutorialFocusTarget = null,
  showMessage,
  companyId,
  companyName,
  onGiaContextChange,
}) => {
  const [activeTab, setActiveTab] = useState('services');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [templateModalState, setTemplateModalState] = useState({ open: false, template: null });
  const [contractModalState, setContractModalState] = useState({ open: false, client: null, contract: null });
  const [savingService, setSavingService] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingContract, setSavingContract] = useState(false);
  const [updatingServiceId, setUpdatingServiceId] = useState('');
  const [payingClosureId, setPayingClosureId] = useState('');
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [transports, setTransports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [closures, setClosures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [liquidationSettings, setLiquidationSettings] = useState({});
  const [officeSettings, setOfficeSettings] = useState({});
  const [serviceListMode, setServiceListMode] = useState('active');
  const [archivedClientFilter, setArchivedClientFilter] = useState('all');

  useEffect(() => {
    if (!tutorialActive || !tutorialFocusTarget) return;
    const timeoutId = window.setTimeout(() => {
      const targetNode = document.querySelector(tutorialFocusTarget);
      if (targetNode) {
        targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
    return () => window.clearTimeout(timeoutId);
  }, [tutorialActive, tutorialFocusTarget]);

  useEffect(() => {
    if (!companyId) return undefined;

    const contactsRef = collection(db, resolveContactsCollectionPath(companyId));
    const servicesRef = collection(db, resolveServicesCollectionPath(companyId));
    const routesRef = collection(db, resolveRoutesCollectionPath(companyId));
    const driversRef = collection(db, resolveDriversCollectionPath(companyId));
    const transportsRef = collection(db, resolveTransportsCollectionPath(companyId));
    const templatesRef = collection(db, resolveServiceTemplatesCollectionPath(companyId));
    const contractsRef = collection(db, resolveServiceContractsCollectionPath(companyId));
    const closuresRef = collection(db, resolveServiceClosuresCollectionPath(companyId));
    const locationsRef = collection(db, resolveServiceLocationsCollectionPath(companyId));
    const liquidationSettingsRef = doc(db, resolveCompanyLiquidationSettingsPath(companyId));
    const officeSettingsRef = doc(db, resolveCompanyOfficeSettingsPath(companyId));

    const unsubscribeContacts = onSnapshot(
      contactsRef,
      (snapshot) => {
        const nextClients = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((contact) => {
            const type = String(contact?.type || '').trim().toLowerCase();
            return type === 'cliente' || type === 'client';
          });
        setClients(nextClients);
      },
      (error) => {
        console.error('Error cargando clientes para Servicio:', error);
        setClients([]);
      }
    );

    const unsubscribeServices = onSnapshot(
      servicesRef,
      (snapshot) => setServices(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      (error) => {
        console.error('Error cargando servicios:', error);
        setServices([]);
      }
    );

    const unsubscribeRoutes = onSnapshot(
      routesRef,
      (snapshot) => setRoutes(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      (error) => {
        console.error('Error cargando rutas para cruzar servicio/logística:', error);
        setRoutes([]);
      }
    );

    const unsubscribeDrivers = onSnapshot(
      driversRef,
      (snapshot) => setDrivers(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      (error) => {
        console.error('Error cargando choferes para Servicio:', error);
        setDrivers([]);
      }
    );

    const unsubscribeTransports = onSnapshot(
      transportsRef,
      (snapshot) => setTransports(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      (error) => {
        console.error('Error cargando transportes para Servicio:', error);
        setTransports([]);
      }
    );

    const unsubscribeTemplates = onSnapshot(
      templatesRef,
      (snapshot) => setTemplates(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      (error) => {
        console.error('Error cargando plantillas de servicio:', error);
        setTemplates([]);
      }
    );

    const unsubscribeContracts = onSnapshot(
      contractsRef,
      (snapshot) => setContracts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      (error) => {
        console.error('Error cargando contratos de servicio:', error);
        setContracts([]);
      }
    );

    const unsubscribeClosures = onSnapshot(
      closuresRef,
      (snapshot) => setClosures(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      (error) => {
        console.error('Error cargando cierres comerciales:', error);
        setClosures([]);
      }
    );

    const unsubscribeLocations = onSnapshot(
      locationsRef,
      (snapshot) => setLocations(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      (error) => {
        console.error('Error cargando destinos compartidos de servicio:', error);
        setLocations([]);
      }
    );

    const unsubscribeLiquidationSettings = onSnapshot(
      liquidationSettingsRef,
      (snapshot) => setLiquidationSettings(snapshot.exists() ? snapshot.data() || {} : {}),
      (error) => {
        console.error('Error cargando configuración de liquidación para Servicio:', error);
        setLiquidationSettings({});
      }
    );

    const unsubscribeOfficeSettings = onSnapshot(
      officeSettingsRef,
      (snapshot) => setOfficeSettings(snapshot.exists() ? snapshot.data() || {} : {}),
      (error) => {
        console.error('Error cargando base operativa para Servicio:', error);
        setOfficeSettings({});
      }
    );

    return () => {
      unsubscribeContacts();
      unsubscribeServices();
      unsubscribeRoutes();
      unsubscribeDrivers();
      unsubscribeTransports();
      unsubscribeTemplates();
      unsubscribeContracts();
      unsubscribeClosures();
      unsubscribeLocations();
      unsubscribeLiquidationSettings();
      unsubscribeOfficeSettings();
    };
  }, [companyId]);

  useEffect(() => {
    if (typeof onGiaContextChange !== 'function') return;
    const activeConfig = TAB_OPTIONS.find((tab) => tab.key === activeTab);
    onGiaContextChange({
      subsectionLabel: 'Servicio',
      focusLabel: activeConfig?.label || 'Panel principal',
    });
  }, [activeTab, onGiaContextChange]);

  const serviceSummary = useMemo(() => {
    const normalizedServices = services.map((service) => ({
      ...service,
      normalizedStatus: String(service?.status || 'pending').trim().toLowerCase(),
      salePriceValue: Math.max(0, Number(service?.salePrice || 0)),
    }));

    const completed = normalizedServices.filter((service) => ['completed', 'invoiced', 'paid'].includes(service.normalizedStatus));
    const pending = normalizedServices.filter((service) => ['pending', 'draft'].includes(service.normalizedStatus));
    const logisticsPending = normalizedServices.filter((service) => service.normalizedStatus === 'pending_logistics');
    const assigned = normalizedServices.filter((service) => ['assigned', 'scheduled'].includes(service.normalizedStatus));
    const inProgress = normalizedServices.filter((service) => ['in_progress', 'in-progress', 'en_curso', 'en-curso'].includes(service.normalizedStatus));
    const billed = normalizedServices.filter((service) => ['invoiced', 'paid'].includes(service.normalizedStatus));

    return {
      pendingCount: pending.length,
      logisticsPendingCount: logisticsPending.length,
      assignedCount: assigned.length,
      inProgressCount: inProgress.length,
      completedCount: completed.length,
      archivedCount: normalizedServices.filter((service) => !ACTIVE_SERVICE_STATUSES.has(service.normalizedStatus)).length,
      activeCount: normalizedServices.filter((service) => ACTIVE_SERVICE_STATUSES.has(service.normalizedStatus)).length,
      billedCount: billed.length,
      billedRevenue: billed.reduce((acc, service) => acc + service.salePriceValue, 0),
    };
  }, [services]);

  const routeStatusById = useMemo(() => {
    const nextMap = new Map();
    routes.forEach((route) => {
      const routeId = String(route?.id || '').trim();
      if (!routeId) return;
      nextMap.set(routeId, getRouteStatus(route));
    });
    return nextMap;
  }, [routes]);

  const serviceCollections = useMemo(() => {
    const orderedServices = services
      .slice()
      .sort((a, b) => getServiceTimelineTime(b) - getServiceTimelineTime(a));

    const active = [];
    const archived = [];
    const review = [];
    const now = Date.now();
    const STALE_ASSIGNED_MS = 18 * 60 * 60 * 1000;

    orderedServices.forEach((service) => {
      const normalizedStatus = normalizeServiceStatus(service?.status);
      const routeId = String(service?.routeId || '').trim();
      const linkedRouteStatus = routeId ? String(routeStatusById.get(routeId) || '').trim().toLowerCase() : '';
      const hasFinancialClosure =
        Boolean(service?.financialClosedAt) ||
        Boolean(service?.revenueRecognizedAt) ||
        Boolean(service?.completedAt) ||
        ['completed', 'invoiced', 'paid', 'cancelled'].includes(normalizedStatus);
      const serviceAgeMs = Math.max(0, now - getServiceTimelineTime(service));
      const isAssignedLike = ['assigned', 'scheduled', 'in_progress', 'in-progress', 'en_curso', 'en-curso'].includes(normalizedStatus);
      const linkedRouteStillActive = ['pending', 'programmed', 'in_progress'].includes(linkedRouteStatus);
      const needsReview =
        isAssignedLike &&
        !hasFinancialClosure &&
        (
          (routeId && linkedRouteStatus && !linkedRouteStillActive) ||
          (!routeId && serviceAgeMs > STALE_ASSIGNED_MS) ||
          (!linkedRouteStatus && serviceAgeMs > STALE_ASSIGNED_MS)
        );

      if (needsReview) {
        review.push(service);
        return;
      }

      if (ACTIVE_SERVICE_STATUSES.has(normalizedStatus) && !hasFinancialClosure) {
        active.push(service);
        return;
      }
      archived.push(service);
    });

    return { active, archived, review };
  }, [routeStatusById, services]);

  const archivedClientOptions = useMemo(() => {
    const options = new Map();
    serviceCollections.archived.forEach((service) => {
      const clientKey = String(service?.clientId || service?.clientNameSnapshot || service?.clientName || '').trim();
      if (!clientKey || options.has(clientKey)) return;
      options.set(clientKey, {
        value: clientKey,
        label: String(service?.clientNameSnapshot || service?.clientName || 'Cliente sin nombre').trim(),
      });
    });
    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [serviceCollections.archived]);

  const filteredArchivedServices = useMemo(() => {
    if (archivedClientFilter === 'all') return serviceCollections.archived;
    return serviceCollections.archived.filter((service) => {
      const clientKey = String(service?.clientId || service?.clientNameSnapshot || service?.clientName || '').trim();
      return clientKey === archivedClientFilter;
    });
  }, [archivedClientFilter, serviceCollections.archived]);

  const contractSummary = useMemo(() => {
    const normalizedContracts = contracts.map((contract) => ({
      ...contract,
      normalizedStatus: String(contract?.status || 'draft').trim().toLowerCase(),
      monthlyFeeValue: Math.max(0, Number(contract?.monthlyFee || 0)),
    }));

    const activeContracts = normalizedContracts.filter((contract) => contract.normalizedStatus === 'active');
    return {
      activeCount: activeContracts.length,
      monthlyRevenue: activeContracts.reduce((acc, contract) => acc + contract.monthlyFeeValue, 0),
    };
  }, [contracts]);

  const panelTitle = TAB_OPTIONS.find((tab) => tab.key === activeTab)?.label || 'Servicio';
  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => buildClientDisplayName(a).localeCompare(buildClientDisplayName(b), 'es')),
    [clients]
  );
  const defaultOperationalOrigin = useMemo(() => {
    const officeName = String(
      officeSettings?.officeName ||
      officeSettings?.name ||
      companyName ||
      companyId ||
      'Base operativa'
    ).trim();
    const lat = Number(officeSettings?.lat);
    const lng = Number(officeSettings?.lng);
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
    const address = String(
      officeSettings?.fullAddress ||
      officeSettings?.address ||
      buildServiceAddressText(officeSettings)
    ).trim();

    if (!address && !hasCoords) return null;

    return {
      id: 'office:default-operational-origin',
      source: 'office',
      label: officeName || 'Base operativa',
      address: address || 'Base operativa de la empresa',
      locality: String(officeSettings?.locality || '').trim(),
      province: String(officeSettings?.province || '').trim(),
      postalCode: String(officeSettings?.postalCode || '').trim(),
      latlng: hasCoords ? { lat, lng } : null,
      searchText: `${officeName} ${address}`.trim().toLowerCase(),
    };
  }, [companyId, companyName, officeSettings]);

  const contractsByClientId = useMemo(() => {
    const nextMap = new Map();
    contracts.forEach((contract) => {
      const clientId = String(contract?.clientId || '').trim();
      if (!clientId || nextMap.has(clientId)) return;
      nextMap.set(clientId, contract);
    });
    return nextMap;
  }, [contracts]);

  const transportsById = useMemo(() => {
    const nextMap = new Map();
    transports.forEach((transport) => {
      const transportId = String(transport?.id || '').trim();
      if (!transportId || nextMap.has(transportId)) return;
      nextMap.set(transportId, transport);
    });
    return nextMap;
  }, [transports]);

  const driversById = useMemo(() => {
    const nextMap = new Map();
    drivers.forEach((driver) => {
      const driverId = String(driver?.id || '').trim();
      if (!driverId || nextMap.has(driverId)) return;
      nextMap.set(driverId, driver);
    });
    return nextMap;
  }, [drivers]);

  const resolveTransportSnapshotName = (transportId = '') => {
    const transport = transportsById.get(String(transportId || '').trim());
    return transport?.name || transport?.displayName || transport?.plate || transport?.id || '';
  };

  const resolveDriverSnapshotName = (driverId = '') => {
    const driver = driversById.get(String(driverId || '').trim());
    if (!driver) return '';
    return [driver?.firstName || driver?.name || '', driver?.lastName || ''].filter(Boolean).join(' ').trim() || driver?.id || '';
  };

  const handleCreateService = async (form, selectedClient) => {
    if (!companyId) {
      showMessage?.('No encontramos una empresa activa para guardar el servicio.', 'error');
      return;
    }

    const creatingNewClient = String(form?.clientMode || 'existing').trim() === 'new';
    const clientId = String(form?.clientId || '').trim();
    const scheduledDate = String(form?.scheduledDate || '').trim();
    let salePriceValue = Number(form?.salePrice || 0);
    let originSnapshot = form?.originSnapshot || null;
    let destinationSnapshot = form?.destinationSnapshot || null;
    let additionalStopSnapshots = Array.isArray(form?.additionalStopSnapshots) ? form.additionalStopSnapshots : [];

    if (creatingNewClient) {
      const newClientName = String(form?.newClientName || '').trim();
      if (!newClientName) {
        showMessage?.('Cargá el nombre del cliente nuevo antes de guardar el servicio.', 'warning');
        return;
      }
    } else if (!clientId || !selectedClient) {
      showMessage?.('Elegí un cliente antes de guardar el servicio.', 'warning');
      return;
    }

    if (!originSnapshot?.address || !destinationSnapshot?.address || !scheduledDate) {
      showMessage?.('Completá origen, destino y fecha para registrar el servicio.', 'warning');
      return;
    }

    const selectedMode = SERVICE_MODE_OPTIONS.find((option) => option.value === form.serviceMode);
    const selectedTransport = SERVICE_TRANSPORT_OPTIONS.find((option) => option.value === form.serviceTransport);
    const selectedPriority = SERVICE_PRIORITY_OPTIONS.find((option) => option.value === form.priority);

    setSavingService(true);
    try {
      if (!hasValidLatLng(originSnapshot?.latlng)) {
        const originCoords = await geocodeAddress(buildServiceAddressText(originSnapshot), showMessage);
        if (!originCoords) {
          showMessage?.('No pudimos validar la geolocalización del origen. Revisá la dirección o cargá coordenadas.', 'error');
          return;
        }
        originSnapshot = {
          ...originSnapshot,
          latlng: originCoords,
        };
      }

      if (!hasValidLatLng(destinationSnapshot?.latlng)) {
        const destinationCoords = await geocodeAddress(buildServiceAddressText(destinationSnapshot), showMessage);
        if (!destinationCoords) {
          showMessage?.('No pudimos validar la geolocalización del destino. Revisá la dirección o cargá coordenadas.', 'error');
          return;
        }
        destinationSnapshot = {
          ...destinationSnapshot,
          latlng: destinationCoords,
        };
      }

      additionalStopSnapshots = await Promise.all(
        additionalStopSnapshots.map(async (stop) => {
          if (hasValidLatLng(stop?.latlng)) return stop;
          const coords = await geocodeAddress(buildServiceAddressText(stop), showMessage);
          if (!coords) {
            throw new Error(`No pudimos validar la geolocalización de la parada ${stop?.label || stop?.address || 'extra'}.`);
          }
          return { ...stop, latlng: coords };
        })
      );

      const servicesRef = collection(db, resolveServicesCollectionPath(companyId));
      const contactsRef = collection(db, resolveContactsCollectionPath(companyId));
      let resolvedClient = selectedClient;
      let resolvedClientId = clientId;
      let resolvedClientName = selectedClient ? buildClientDisplayName(selectedClient) : '';

      if (creatingNewClient) {
        let clientAddressSnapshot = form?.newClientAddressSnapshot || destinationSnapshot || null;
        if (clientAddressSnapshot?.address && !hasValidLatLng(clientAddressSnapshot?.latlng)) {
          const clientCoords = await geocodeAddress(buildServiceAddressText(clientAddressSnapshot), showMessage);
          if (clientCoords) {
            clientAddressSnapshot = { ...clientAddressSnapshot, latlng: clientCoords };
          }
        }
        const primaryAddress = buildClientAddressFromSnapshot(clientAddressSnapshot);
        const contactPeople = [
          {
            name: String(form?.newClientContactName || '').trim(),
            phone: String(form?.newClientPhone || '').trim(),
            email: String(form?.newClientEmail || '').trim(),
          },
        ].filter((item) => item.name || item.phone || item.email);

        const newClientDoc = await addDoc(contactsRef, {
          companyId,
          companyName: companyName || companyId,
          name: String(form?.newClientName || '').trim(),
          type: 'cliente',
          addresses: primaryAddress ? [primaryAddress] : [],
          contacts: contactPeople,
          phone: String(form?.newClientPhone || '').trim(),
          email: String(form?.newClientEmail || '').trim(),
          contactName: String(form?.newClientContactName || '').trim(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        resolvedClientId = newClientDoc.id;
        resolvedClient = {
          id: newClientDoc.id,
          name: String(form?.newClientName || '').trim(),
          type: 'cliente',
          contacts: contactPeople,
          addresses: primaryAddress ? [primaryAddress] : [],
          phone: String(form?.newClientPhone || '').trim(),
          email: String(form?.newClientEmail || '').trim(),
          contactName: String(form?.newClientContactName || '').trim(),
        };
        resolvedClientName = buildClientDisplayName(resolvedClient);
      }

      const linkedContract = contractsByClientId.get(String(resolvedClientId || '').trim()) || null;
      const outsideContract = resolveOutsideContractFlag({
        linkedContract,
        frequency: form.frequency || 'once',
      });
      const primaryClientContact = resolvePrimaryClientContact(resolvedClient);
      const commercialSnapshot = resolveCommercialSnapshot({
        linkedContract,
        form,
        forceOutsideContract: outsideContract,
      });
      const billingCadenceSnapshot = resolveServiceBillingCadence({
        linkedContract,
        frequency: form.frequency || 'once',
        outsideContract,
      });
      const linkedContractModel = SERVICE_CONTRACT_MODEL_OPTIONS.find(
        (option) => option.value === commercialSnapshot.commercialModel
      );
      const linkedPricingRule = SERVICE_PRICING_RULE_OPTIONS.find(
        (option) => option.value === commercialSnapshot.pricingRule
      );
      const locationWrites = [];
      if (form?.originMode === 'manual' && form?.originState?.saveToSharedBase && originSnapshot?.address) {
        locationWrites.push(
          addDoc(collection(db, resolveServiceLocationsCollectionPath(companyId)), {
            companyId,
            label: originSnapshot.label,
            address: originSnapshot.address,
            locality: originSnapshot.locality || '',
            province: originSnapshot.province || '',
            postalCode: originSnapshot.postalCode || '',
            latlng: originSnapshot.latlng || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
        );
      }
      if (form?.destinationMode === 'manual' && form?.destinationState?.saveToSharedBase && destinationSnapshot?.address) {
        locationWrites.push(
          addDoc(collection(db, resolveServiceLocationsCollectionPath(companyId)), {
            companyId,
            label: destinationSnapshot.label,
            address: destinationSnapshot.address,
            locality: destinationSnapshot.locality || '',
            province: destinationSnapshot.province || '',
            postalCode: destinationSnapshot.postalCode || '',
            latlng: destinationSnapshot.latlng || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
        );
      }
      additionalStopSnapshots.forEach((stop, index) => {
        if (Array.isArray(form?.additionalStops) && form.additionalStops[index]?.mode === 'manual' && form.additionalStops[index]?.saveToSharedBase && stop?.address) {
          locationWrites.push(
            addDoc(collection(db, resolveServiceLocationsCollectionPath(companyId)), {
              companyId,
              label: stop.label,
              address: stop.address,
              locality: stop.locality || '',
              province: stop.province || '',
              postalCode: stop.postalCode || '',
              latlng: stop.latlng || null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            })
          );
        }
      });
      if (locationWrites.length) {
        await Promise.all(locationWrites);
      }

      const contactSyncs = [];
      if (form?.destinationMode === 'manual' && form?.destinationState?.saveToSharedBase && destinationSnapshot?.address) {
        contactSyncs.push(
          syncSharedContactDestination({
            companyId,
            companyName,
            snapshot: destinationSnapshot,
          })
        );
      }
      additionalStopSnapshots.forEach((stop, index) => {
        if (Array.isArray(form?.additionalStops) && form.additionalStops[index]?.mode === 'manual' && form.additionalStops[index]?.saveToSharedBase && stop?.address) {
          contactSyncs.push(
            syncSharedContactDestination({
              companyId,
              companyName,
              snapshot: stop,
            })
          );
        }
      });
      if (contactSyncs.length) {
        await Promise.all(contactSyncs);
      }

      const basePayload = {
        companyId,
        clientId: resolvedClientId,
        clientNameSnapshot: resolvedClientName,
        clientContactPersonSnapshot: primaryClientContact.name,
        clientPhoneSnapshot: primaryClientContact.phone,
        clientEmailSnapshot: primaryClientContact.email,
        contactId: resolvedClient?.contactId || resolvedClient?.id || '',
        contactNameSnapshot: resolvedClientName,
        serviceMode: form.serviceMode || 'puntual',
        serviceModeLabel: selectedMode?.label || 'Puntual',
        serviceType: form.serviceTransport || 'camioneta',
        serviceTypeLabel: selectedTransport?.label || 'Camioneta',
        serviceTransport: form.serviceTransport || 'camioneta',
        serviceTransportLabel: selectedTransport?.label || 'Camioneta',
        serviceTask: form.serviceTask || 'servicio',
        serviceTaskLabel: form.serviceTaskLabel || 'Servicio',
        frequency: form.frequency || 'once',
        origin: originSnapshot.label || originSnapshot.address,
        destination: destinationSnapshot.label || destinationSnapshot.address,
        originSnapshot,
        destinationSnapshot,
        additionalStops: additionalStopSnapshots,
        priority: form.priority || 'normal',
        priorityLabel: selectedPriority?.label || 'Normal',
        salePrice: salePriceValue,
        contractId: linkedContract?.id || '',
        contractNameSnapshot: linkedContract?.name || '',
        commercialModel: commercialSnapshot.commercialModel || '',
        commercialModelLabel: linkedContractModel?.label || '',
        billingCadenceSnapshot,
        billingCadenceLabel: resolveBillingCadenceLabel(billingCadenceSnapshot),
        outsideContract,
        billingDayOfWeek: linkedContract?.billingDayOfWeek || '',
        billingDayOfMonth: linkedContract?.billingDayOfMonth || '',
        serviceDaysOfWeek: Array.isArray(linkedContract?.serviceDaysOfWeek) ? linkedContract.serviceDaysOfWeek : [],
        serviceTimeWindowStart: linkedContract?.serviceTimeWindowStart || '',
        serviceTimeWindowEnd: linkedContract?.serviceTimeWindowEnd || '',
        serviceStartDate: linkedContract?.serviceStartDate || '',
        serviceEndDate: linkedContract?.serviceEndDate || '',
        pricingRule: commercialSnapshot.pricingRule || '',
        pricingRuleLabel: linkedPricingRule?.label || '',
        transportAssignmentModeSnapshot: linkedContract?.transportAssignmentMode || 'free',
        fixedTransportIdSnapshot: linkedContract?.fixedTransportId || '',
        fixedTransportNameSnapshot: linkedContract?.fixedTransportNameSnapshot || resolveTransportSnapshotName(linkedContract?.fixedTransportId),
        suggestedTransportIdSnapshot: linkedContract?.suggestedTransportId || '',
        suggestedTransportNameSnapshot: linkedContract?.suggestedTransportNameSnapshot || resolveTransportSnapshotName(linkedContract?.suggestedTransportId),
        driverAssignmentModeSnapshot: linkedContract?.driverAssignmentMode || 'free',
        fixedDriverIdSnapshot: linkedContract?.fixedDriverId || '',
        fixedDriverNameSnapshot: linkedContract?.fixedDriverNameSnapshot || resolveDriverSnapshotName(linkedContract?.fixedDriverId),
        suggestedDriverIdSnapshot: linkedContract?.suggestedDriverId || '',
        suggestedDriverNameSnapshot: linkedContract?.suggestedDriverNameSnapshot || resolveDriverSnapshotName(linkedContract?.suggestedDriverId),
        monthlyFeeSnapshot: commercialSnapshot.monthlyFeeSnapshot,
        agreedPricePerTripSnapshot: commercialSnapshot.agreedPricePerTripSnapshot,
        fixedMarginAmountSnapshot: commercialSnapshot.fixedMarginAmountSnapshot,
        marginPresetSnapshot: commercialSnapshot.marginPresetSnapshot,
        marginPercentSnapshot: commercialSnapshot.marginPercentSnapshot,
        recognitionPending: false,
        recognizedInBalance: false,
        revenueRecognizedAt: null,
        recognizedAt: null,
        pendingClosureKey: '',
        pendingClosurePeriodStart: null,
        pendingClosurePeriodEnd: null,
        notes: String(form?.notes || '').trim(),
        scheduledDate,
        date: scheduledDate,
        status: 'pending',
        statusLabel: 'Pendiente',
        executionSource: 'service',
        routeId: '',
        driverId:
          (linkedContract?.driverAssignmentMode || 'free') === 'fixed'
            ? linkedContract?.fixedDriverId || ''
            : '',
        transportId: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const estimateSnapshot = buildEstimatedPricingSnapshot(basePayload, liquidationSettings);
      salePriceValue = estimateSnapshot.estimatedTotalToCharge || 0;

      await addDoc(servicesRef, {
        ...basePayload,
        salePrice: salePriceValue,
        ...estimateSnapshot,
      });

      if (form.frequency && form.frequency !== 'once' && form.saveAsTemplate) {
        const selectedFrequency = SERVICE_TEMPLATE_FREQUENCY_OPTIONS.find((option) => option.value === form.frequency);
        await addDoc(collection(db, resolveServiceTemplatesCollectionPath(companyId)), {
          companyId,
          clientId: resolvedClientId,
          clientNameSnapshot: resolvedClientName,
          clientContactPersonSnapshot: primaryClientContact.name,
          clientPhoneSnapshot: primaryClientContact.phone,
          clientEmailSnapshot: primaryClientContact.email,
          serviceMode: form.serviceMode || 'puntual',
          serviceModeLabel: selectedMode?.label || 'Puntual',
          serviceType: form.serviceTransport || 'camioneta',
          serviceTypeLabel: selectedTransport?.label || 'Camioneta',
          serviceTransport: form.serviceTransport || 'camioneta',
          serviceTransportLabel: selectedTransport?.label || 'Camioneta',
          serviceTask: form.serviceTask || 'servicio',
          serviceTaskLabel: form.serviceTaskLabel || 'Servicio',
          frequency: form.frequency || 'manual',
          frequencyLabel: selectedFrequency?.label || 'A pedido',
          priority: form.priority || 'normal',
          priorityLabel: selectedPriority?.label || 'Normal',
          origin: originSnapshot.label || originSnapshot.address,
          destination: destinationSnapshot.label || destinationSnapshot.address,
          originSnapshot,
          destinationSnapshot,
          additionalStops: additionalStopSnapshots,
          notes: String(form?.notes || '').trim(),
          status: 'active',
          contractId: linkedContract?.id || '',
          contractNameSnapshot: linkedContract?.name || '',
          commercialModel: commercialSnapshot.commercialModel || '',
          commercialModelLabel: linkedContractModel?.label || '',
          billingCadenceSnapshot,
          billingCadenceLabel: resolveBillingCadenceLabel(billingCadenceSnapshot),
          outsideContract,
          pricingRule: commercialSnapshot.pricingRule || '',
          pricingRuleLabel: linkedPricingRule?.label || '',
          billingDayOfWeek: linkedContract?.billingDayOfWeek || '',
          billingDayOfMonth: linkedContract?.billingDayOfMonth || '',
          serviceDaysOfWeek: Array.isArray(linkedContract?.serviceDaysOfWeek) ? linkedContract.serviceDaysOfWeek : [],
          serviceTimeWindowStart: linkedContract?.serviceTimeWindowStart || '',
          serviceTimeWindowEnd: linkedContract?.serviceTimeWindowEnd || '',
          serviceStartDate: linkedContract?.serviceStartDate || '',
          serviceEndDate: linkedContract?.serviceEndDate || '',
          transportAssignmentModeSnapshot: linkedContract?.transportAssignmentMode || 'free',
          fixedTransportIdSnapshot: linkedContract?.fixedTransportId || '',
          fixedTransportNameSnapshot: linkedContract?.fixedTransportNameSnapshot || resolveTransportSnapshotName(linkedContract?.fixedTransportId),
          suggestedTransportIdSnapshot: linkedContract?.suggestedTransportId || '',
          suggestedTransportNameSnapshot: linkedContract?.suggestedTransportNameSnapshot || resolveTransportSnapshotName(linkedContract?.suggestedTransportId),
          driverAssignmentModeSnapshot: linkedContract?.driverAssignmentMode || 'free',
          fixedDriverIdSnapshot: linkedContract?.fixedDriverId || '',
          fixedDriverNameSnapshot: linkedContract?.fixedDriverNameSnapshot || resolveDriverSnapshotName(linkedContract?.fixedDriverId),
          suggestedDriverIdSnapshot: linkedContract?.suggestedDriverId || '',
          suggestedDriverNameSnapshot: linkedContract?.suggestedDriverNameSnapshot || resolveDriverSnapshotName(linkedContract?.suggestedDriverId),
          monthlyFeeSnapshot: commercialSnapshot.monthlyFeeSnapshot,
          agreedPricePerTripSnapshot: commercialSnapshot.agreedPricePerTripSnapshot,
          fixedMarginAmountSnapshot: commercialSnapshot.fixedMarginAmountSnapshot,
          marginPresetSnapshot: commercialSnapshot.marginPresetSnapshot,
          marginPercentSnapshot: commercialSnapshot.marginPercentSnapshot,
          salePrice: salePriceValue,
          ...estimateSnapshot,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      setCreateModalOpen(false);
      setActiveTab('services');
      showMessage?.(`Servicio guardado para ${resolvedClientName}. Ya quedó listo para seguir con asignación.`, 'success');
    } catch (error) {
      console.error('Error creando servicio:', error);
      showMessage?.('No pudimos guardar el servicio. Revisemos los datos e intentemos otra vez.', 'error');
    } finally {
      setSavingService(false);
    }
  };

  const handleCreateServiceClient = async (payload = {}) => {
    if (!companyId) {
      showMessage?.('No encontramos una empresa activa para guardar el cliente.', 'error');
      return null;
    }

    const clientName = String(payload?.newClientName || '').trim();
    if (!clientName) {
      showMessage?.('Cargá el nombre del cliente antes de guardarlo.', 'warning');
      return null;
    }

    let clientAddressSnapshot = payload?.newClientAddressSnapshot || null;
    if (!clientAddressSnapshot?.address) {
      showMessage?.('Completá la dirección principal del cliente antes de guardarlo.', 'warning');
      return null;
    }

    if (!hasValidLatLng(clientAddressSnapshot?.latlng)) {
      const clientCoords = await geocodeAddress(buildServiceAddressText(clientAddressSnapshot), showMessage);
      if (!clientCoords) {
        showMessage?.('No pudimos validar la geolocalización del cliente. Revisá la dirección.', 'error');
        return null;
      }
      clientAddressSnapshot = { ...clientAddressSnapshot, latlng: clientCoords };
    }

    const contactsRef = collection(db, resolveContactsCollectionPath(companyId));
    const primaryAddress = buildClientAddressFromSnapshot(clientAddressSnapshot);
    const contactPeople = [
      {
        name: String(payload?.newClientContactName || '').trim(),
        phone: String(payload?.newClientPhone || '').trim(),
        email: String(payload?.newClientEmail || '').trim(),
      },
    ].filter((item) => item.name || item.phone || item.email);

    const newClientDoc = await addDoc(contactsRef, {
      companyId,
      companyName: companyName || companyId,
      name: clientName,
      type: 'cliente',
      addresses: primaryAddress ? [primaryAddress] : [],
      contacts: contactPeople,
      phone: String(payload?.newClientPhone || '').trim(),
      email: String(payload?.newClientEmail || '').trim(),
      contactName: String(payload?.newClientContactName || '').trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const createdClient = {
      id: newClientDoc.id,
      name: clientName,
      type: 'cliente',
      contacts: contactPeople,
      addresses: primaryAddress ? [primaryAddress] : [],
      phone: String(payload?.newClientPhone || '').trim(),
      email: String(payload?.newClientEmail || '').trim(),
      contactName: String(payload?.newClientContactName || '').trim(),
    };

    showMessage?.(`Cliente ${buildClientDisplayName(createdClient)} guardado en la base general.`, 'success');
    return createdClient;
  };

  const handleOpenClientContractEditor = (client, contract = null) => {
    setContractModalState({ open: true, client, contract });
  };

  const handleCreateTemplate = async (form) => {
    if (!companyId) {
      showMessage?.('No encontramos una empresa activa para guardar la plantilla.', 'error');
      return;
    }

    const creatingNewClient = String(form?.clientMode || 'existing').trim() === 'new';
    let resolvedClient = form?.selectedClient || null;
    let resolvedClientId = String(form?.clientId || '').trim();
    let resolvedClientName = resolvedClient ? buildClientDisplayName(resolvedClient) : '';
    let originSnapshot = form?.originSnapshot || null;
    let destinationSnapshot = form?.destinationSnapshot || null;
    let additionalStopSnapshots = Array.isArray(form?.additionalStopSnapshots) ? form.additionalStopSnapshots : [];

    if (creatingNewClient) {
      const newClientName = String(form?.newClientName || '').trim();
      if (!newClientName) {
        showMessage?.('Cargá el nombre del cliente antes de guardar la plantilla.', 'warning');
        return;
      }
    } else if (!resolvedClientId || !resolvedClient) {
      showMessage?.('Elegí un cliente antes de guardar la plantilla.', 'warning');
      return;
    }

    if (!originSnapshot?.address || !destinationSnapshot?.address) {
      showMessage?.('Completá origen y destino para guardar la plantilla.', 'warning');
      return;
    }

    setSavingTemplate(true);
    try {
      if (!hasValidLatLng(originSnapshot?.latlng)) {
        const originCoords = await geocodeAddress(buildServiceAddressText(originSnapshot), showMessage);
        if (!originCoords) {
          showMessage?.('No pudimos validar la geolocalización del origen de la plantilla.', 'error');
          return;
        }
        originSnapshot = { ...originSnapshot, latlng: originCoords };
      }

      if (!hasValidLatLng(destinationSnapshot?.latlng)) {
        const destinationCoords = await geocodeAddress(buildServiceAddressText(destinationSnapshot), showMessage);
        if (!destinationCoords) {
          showMessage?.('No pudimos validar la geolocalización del destino de la plantilla.', 'error');
          return;
        }
        destinationSnapshot = { ...destinationSnapshot, latlng: destinationCoords };
      }

      additionalStopSnapshots = await Promise.all(
        additionalStopSnapshots.map(async (stop) => {
          if (hasValidLatLng(stop?.latlng)) return stop;
          const coords = await geocodeAddress(buildServiceAddressText(stop), showMessage);
          if (!coords) {
            throw new Error(`No pudimos validar la geolocalización de la parada ${stop?.label || stop?.address || 'extra'}.`);
          }
          return { ...stop, latlng: coords };
        })
      );

      const contactsRef = collection(db, resolveContactsCollectionPath(companyId));

      if (creatingNewClient) {
        let clientAddressSnapshot = form?.newClientAddressSnapshot || destinationSnapshot || null;
        if (clientAddressSnapshot?.address && !hasValidLatLng(clientAddressSnapshot?.latlng)) {
          const clientCoords = await geocodeAddress(buildServiceAddressText(clientAddressSnapshot), showMessage);
          if (clientCoords) {
            clientAddressSnapshot = { ...clientAddressSnapshot, latlng: clientCoords };
          }
        }
        const primaryAddress = buildClientAddressFromSnapshot(clientAddressSnapshot);
        const contactPeople = [
          {
            name: String(form?.newClientContactName || '').trim(),
            phone: String(form?.newClientPhone || '').trim(),
            email: String(form?.newClientEmail || '').trim(),
          },
        ].filter((item) => item.name || item.phone || item.email);

        const newClientDoc = await addDoc(contactsRef, {
          companyId,
          companyName: companyName || companyId,
          name: String(form?.newClientName || '').trim(),
          type: 'cliente',
          addresses: primaryAddress ? [primaryAddress] : [],
          contacts: contactPeople,
          phone: String(form?.newClientPhone || '').trim(),
          email: String(form?.newClientEmail || '').trim(),
          contactName: String(form?.newClientContactName || '').trim(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        resolvedClientId = newClientDoc.id;
        resolvedClient = {
          id: newClientDoc.id,
          name: String(form?.newClientName || '').trim(),
          type: 'cliente',
          contacts: contactPeople,
          addresses: primaryAddress ? [primaryAddress] : [],
          phone: String(form?.newClientPhone || '').trim(),
          email: String(form?.newClientEmail || '').trim(),
          contactName: String(form?.newClientContactName || '').trim(),
        };
        resolvedClientName = buildClientDisplayName(resolvedClient);
      }

      const selectedMode = SERVICE_MODE_OPTIONS.find((option) => option.value === form.serviceMode);
      const selectedTransport = SERVICE_TRANSPORT_OPTIONS.find((option) => option.value === form.serviceTransport);
      const selectedPriority = SERVICE_PRIORITY_OPTIONS.find((option) => option.value === form.priority);
      const selectedFrequency = SERVICE_TEMPLATE_FREQUENCY_OPTIONS.find((option) => option.value === form.frequency);
      const linkedContract = contractsByClientId.get(String(resolvedClientId || '').trim()) || null;
      const outsideContract = resolveOutsideContractFlag({
        linkedContract,
        frequency: form.frequency || 'manual',
      });
      const primaryClientContact = resolvePrimaryClientContact(resolvedClient);
      const commercialSnapshot = resolveCommercialSnapshot({
        linkedContract,
        form,
        forceOutsideContract: outsideContract,
      });
      const billingCadenceSnapshot = resolveServiceBillingCadence({
        linkedContract,
        frequency: form.frequency || 'manual',
        outsideContract,
      });
      const linkedContractModel = SERVICE_CONTRACT_MODEL_OPTIONS.find(
        (option) => option.value === commercialSnapshot.commercialModel
      );
      const linkedPricingRule = SERVICE_PRICING_RULE_OPTIONS.find(
        (option) => option.value === commercialSnapshot.pricingRule
      );

      const locationWrites = [];
      if (form?.originMode === 'manual' && form?.originState?.saveToSharedBase && originSnapshot?.address) {
        locationWrites.push(
          addDoc(collection(db, resolveServiceLocationsCollectionPath(companyId)), {
            companyId,
            label: originSnapshot.label,
            address: originSnapshot.address,
            locality: originSnapshot.locality || '',
            province: originSnapshot.province || '',
            postalCode: originSnapshot.postalCode || '',
            latlng: originSnapshot.latlng || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
        );
      }
      if (form?.destinationMode === 'manual' && form?.destinationState?.saveToSharedBase && destinationSnapshot?.address) {
        locationWrites.push(
          addDoc(collection(db, resolveServiceLocationsCollectionPath(companyId)), {
            companyId,
            label: destinationSnapshot.label,
            address: destinationSnapshot.address,
            locality: destinationSnapshot.locality || '',
            province: destinationSnapshot.province || '',
            postalCode: destinationSnapshot.postalCode || '',
            latlng: destinationSnapshot.latlng || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
        );
      }
      if (locationWrites.length) {
        await Promise.all(locationWrites);
      }

      const contactSyncs = [];
      if (form?.destinationMode === 'manual' && form?.destinationState?.saveToSharedBase && destinationSnapshot?.address) {
        contactSyncs.push(
          syncSharedContactDestination({
            companyId,
            companyName,
            snapshot: destinationSnapshot,
          })
        );
      }
      additionalStopSnapshots.forEach((stop, index) => {
        if (Array.isArray(form?.additionalStops) && form.additionalStops[index]?.mode === 'manual' && form.additionalStops[index]?.saveToSharedBase && stop?.address) {
          contactSyncs.push(
            syncSharedContactDestination({
              companyId,
              companyName,
              snapshot: stop,
            })
          );
        }
      });
      if (contactSyncs.length) {
        await Promise.all(contactSyncs);
      }

      const basePayload = {
        companyId,
        clientId: resolvedClientId,
        clientNameSnapshot: resolvedClientName,
        clientContactPersonSnapshot: primaryClientContact.name,
        clientPhoneSnapshot: primaryClientContact.phone,
        clientEmailSnapshot: primaryClientContact.email,
        serviceMode: form.serviceMode || 'puntual',
        serviceModeLabel: selectedMode?.label || 'Puntual',
        serviceType: form.serviceTransport || 'camioneta',
        serviceTypeLabel: selectedTransport?.label || 'Camioneta',
        serviceTransport: form.serviceTransport || 'camioneta',
        serviceTransportLabel: selectedTransport?.label || 'Camioneta',
        serviceTask: form.serviceTask || 'servicio',
        serviceTaskLabel: form.serviceTaskLabel || 'Servicio',
        frequency: form.frequency || 'manual',
        frequencyLabel: selectedFrequency?.label || 'A pedido',
        priority: form.priority || 'normal',
        priorityLabel: selectedPriority?.label || 'Normal',
        salePrice: 0,
        origin: originSnapshot.label || originSnapshot.address,
        destination: destinationSnapshot.label || destinationSnapshot.address,
        originSnapshot,
        destinationSnapshot,
        notes: String(form?.notes || '').trim(),
        status: 'active',
        additionalStops: additionalStopSnapshots,
        contractId: linkedContract?.id || '',
        contractNameSnapshot: linkedContract?.name || '',
        commercialModel: commercialSnapshot.commercialModel || '',
        commercialModelLabel: linkedContractModel?.label || '',
        billingCadenceSnapshot,
        billingCadenceLabel: resolveBillingCadenceLabel(billingCadenceSnapshot),
        outsideContract,
        pricingRule: commercialSnapshot.pricingRule || '',
        pricingRuleLabel: linkedPricingRule?.label || '',
        serviceDaysOfWeek: Array.isArray(linkedContract?.serviceDaysOfWeek) ? linkedContract.serviceDaysOfWeek : [],
        serviceTimeWindowStart: linkedContract?.serviceTimeWindowStart || '',
        serviceTimeWindowEnd: linkedContract?.serviceTimeWindowEnd || '',
        serviceStartDate: linkedContract?.serviceStartDate || '',
        serviceEndDate: linkedContract?.serviceEndDate || '',
        transportAssignmentModeSnapshot: linkedContract?.transportAssignmentMode || 'free',
        fixedTransportIdSnapshot: linkedContract?.fixedTransportId || '',
        fixedTransportNameSnapshot: linkedContract?.fixedTransportNameSnapshot || resolveTransportSnapshotName(linkedContract?.fixedTransportId),
        suggestedTransportIdSnapshot: linkedContract?.suggestedTransportId || '',
        suggestedTransportNameSnapshot: linkedContract?.suggestedTransportNameSnapshot || resolveTransportSnapshotName(linkedContract?.suggestedTransportId),
        driverAssignmentModeSnapshot: linkedContract?.driverAssignmentMode || 'free',
        fixedDriverIdSnapshot: linkedContract?.fixedDriverId || '',
        fixedDriverNameSnapshot: linkedContract?.fixedDriverNameSnapshot || resolveDriverSnapshotName(linkedContract?.fixedDriverId),
        suggestedDriverIdSnapshot: linkedContract?.suggestedDriverId || '',
        suggestedDriverNameSnapshot: linkedContract?.suggestedDriverNameSnapshot || resolveDriverSnapshotName(linkedContract?.suggestedDriverId),
        monthlyFeeSnapshot: commercialSnapshot.monthlyFeeSnapshot,
        agreedPricePerTripSnapshot: commercialSnapshot.agreedPricePerTripSnapshot,
        fixedMarginAmountSnapshot: commercialSnapshot.fixedMarginAmountSnapshot,
        marginPresetSnapshot: commercialSnapshot.marginPresetSnapshot,
        marginPercentSnapshot: commercialSnapshot.marginPercentSnapshot,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const estimateSnapshot = buildEstimatedPricingSnapshot(basePayload, liquidationSettings);
      const payload = {
        ...basePayload,
        salePrice: estimateSnapshot.estimatedTotalToCharge || 0,
        ...estimateSnapshot,
      };

      if (templateModalState.template?.id) {
        await updateDoc(
          doc(db, resolveServiceTemplatesCollectionPath(companyId), templateModalState.template.id),
          payload
        );
      } else {
        await addDoc(collection(db, resolveServiceTemplatesCollectionPath(companyId)), payload);
      }

      setTemplateModalState({ open: false, template: null });
      setActiveTab('scheduled');
      showMessage?.(`La plantilla de ${resolvedClientName} quedó guardada para reutilizarla con un clic.`, 'success');
    } catch (error) {
      console.error('Error creando plantilla de servicio:', error);
      showMessage?.('No pudimos guardar la plantilla. Revisemos los datos e intentemos otra vez.', 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleGenerateServiceFromTemplate = async (template) => {
    if (!companyId || !template?.id) {
      showMessage?.('No encontramos la empresa o la plantilla para generar el servicio.', 'error');
      return;
    }

    setUpdatingServiceId(template.id);
    try {
      const today = new Date();
      const scheduledDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const linkedContract = contractsByClientId.get(String(template.clientId || '').trim()) || null;
      const outsideContract = Boolean(template?.outsideContract);
      const billingCadenceSnapshot = resolveServiceBillingCadence({
        linkedContract,
        frequency: template.frequency || 'manual',
        outsideContract,
        explicitCadence: template?.billingCadenceSnapshot || template?.billingCadence,
      });
      const commercialSnapshot = resolveCommercialSnapshot({
        linkedContract,
        form: {
          useClientPricing: !outsideContract,
          commercialModel: template?.commercialModel || 'trip_rate',
          pricingRule: template?.pricingRule || 'manual_total',
          monthlyFee: template?.monthlyFeeSnapshot || template?.monthlyFee || 0,
          agreedPricePerTrip: template?.agreedPricePerTripSnapshot || template?.agreedPricePerTrip || 0,
          fixedMarginAmount: template?.fixedMarginAmountSnapshot || template?.fixedMarginAmount || 0,
          marginPreset: template?.marginPresetSnapshot || template?.marginPreset || 'normal',
          marginPercent: template?.marginPercentSnapshot || template?.marginPercent || 0,
        },
        forceOutsideContract: outsideContract,
      });
      const linkedContractModel = SERVICE_CONTRACT_MODEL_OPTIONS.find(
        (option) => option.value === commercialSnapshot.commercialModel
      );
      const linkedPricingRule = SERVICE_PRICING_RULE_OPTIONS.find(
        (option) => option.value === commercialSnapshot.pricingRule
      );
      const basePayload = {
        companyId,
        clientId: template.clientId || '',
        clientNameSnapshot: template.clientNameSnapshot || '',
        clientContactPersonSnapshot: template.clientContactPersonSnapshot || '',
        clientPhoneSnapshot: template.clientPhoneSnapshot || '',
        clientEmailSnapshot: template.clientEmailSnapshot || '',
        contactId: template.clientId || '',
        contactNameSnapshot: template.clientNameSnapshot || '',
        serviceMode: template.serviceMode || 'puntual',
        serviceModeLabel: template.serviceModeLabel || 'Puntual',
        serviceType: template.serviceTransport || template.serviceType || 'camioneta',
        serviceTypeLabel: template.serviceTransportLabel || template.serviceTypeLabel || 'Camioneta',
        serviceTransport: template.serviceTransport || template.serviceType || 'camioneta',
        serviceTransportLabel: template.serviceTransportLabel || template.serviceTypeLabel || 'Camioneta',
        serviceTask: template.serviceTask || 'retiro_entrega',
        serviceTaskLabel: template.serviceTaskLabel || 'Retiro y entrega',
        origin: template.origin || template.originSnapshot?.label || template.originSnapshot?.address || '',
        destination: template.destination || template.destinationSnapshot?.label || template.destinationSnapshot?.address || '',
        originSnapshot: template.originSnapshot || null,
        destinationSnapshot: template.destinationSnapshot || null,
        additionalStops: Array.isArray(template.additionalStops) ? template.additionalStops : [],
        priority: template.priority || 'normal',
        priorityLabel: template.priorityLabel || 'Normal',
        salePrice: 0,
        contractId: linkedContract?.id || '',
        contractNameSnapshot: linkedContract?.name || '',
        commercialModel: commercialSnapshot.commercialModel || '',
        commercialModelLabel: linkedContractModel?.label || '',
        billingCadenceSnapshot,
        billingCadenceLabel: resolveBillingCadenceLabel(billingCadenceSnapshot),
        outsideContract,
        pricingRule: commercialSnapshot.pricingRule || '',
        pricingRuleLabel: linkedPricingRule?.label || '',
        serviceDaysOfWeek: Array.isArray(template?.serviceDaysOfWeek) ? template.serviceDaysOfWeek : [],
        serviceTimeWindowStart: template?.serviceTimeWindowStart || '',
        serviceTimeWindowEnd: template?.serviceTimeWindowEnd || '',
        serviceStartDate: template?.serviceStartDate || '',
        serviceEndDate: template?.serviceEndDate || '',
        transportAssignmentModeSnapshot: template?.transportAssignmentModeSnapshot || linkedContract?.transportAssignmentMode || 'free',
        fixedTransportIdSnapshot: template?.fixedTransportIdSnapshot || linkedContract?.fixedTransportId || '',
        fixedTransportNameSnapshot: template?.fixedTransportNameSnapshot || linkedContract?.fixedTransportNameSnapshot || resolveTransportSnapshotName(template?.fixedTransportIdSnapshot || linkedContract?.fixedTransportId),
        suggestedTransportIdSnapshot: template?.suggestedTransportIdSnapshot || linkedContract?.suggestedTransportId || '',
        suggestedTransportNameSnapshot: template?.suggestedTransportNameSnapshot || linkedContract?.suggestedTransportNameSnapshot || resolveTransportSnapshotName(template?.suggestedTransportIdSnapshot || linkedContract?.suggestedTransportId),
        driverAssignmentModeSnapshot: template?.driverAssignmentModeSnapshot || linkedContract?.driverAssignmentMode || 'free',
        fixedDriverIdSnapshot: template?.fixedDriverIdSnapshot || linkedContract?.fixedDriverId || '',
        fixedDriverNameSnapshot: template?.fixedDriverNameSnapshot || linkedContract?.fixedDriverNameSnapshot || resolveDriverSnapshotName(template?.fixedDriverIdSnapshot || linkedContract?.fixedDriverId),
        suggestedDriverIdSnapshot: template?.suggestedDriverIdSnapshot || linkedContract?.suggestedDriverId || '',
        suggestedDriverNameSnapshot: template?.suggestedDriverNameSnapshot || linkedContract?.suggestedDriverNameSnapshot || resolveDriverSnapshotName(template?.suggestedDriverIdSnapshot || linkedContract?.suggestedDriverId),
        monthlyFeeSnapshot: commercialSnapshot.monthlyFeeSnapshot,
        agreedPricePerTripSnapshot: commercialSnapshot.agreedPricePerTripSnapshot,
        fixedMarginAmountSnapshot: commercialSnapshot.fixedMarginAmountSnapshot,
        marginPresetSnapshot: commercialSnapshot.marginPresetSnapshot,
        marginPercentSnapshot: commercialSnapshot.marginPercentSnapshot,
        recognitionPending: false,
        recognizedInBalance: false,
        revenueRecognizedAt: null,
        recognizedAt: null,
        pendingClosureKey: '',
        pendingClosurePeriodStart: null,
        pendingClosurePeriodEnd: null,
        notes: String(template?.notes || '').trim(),
        scheduledDate,
        date: scheduledDate,
        status: 'pending',
        statusLabel: 'Pendiente',
        executionSource: 'service_template',
        templateId: template.id,
        templateFrequency: template.frequency || 'manual',
        templateFrequencyLabel: template.frequencyLabel || 'A pedido',
        routeId: '',
        driverId:
          (template?.driverAssignmentModeSnapshot || linkedContract?.driverAssignmentMode) === 'fixed'
            ? template?.fixedDriverIdSnapshot || linkedContract?.fixedDriverId || ''
            : '',
        transportId:
          (template?.transportAssignmentModeSnapshot || linkedContract?.transportAssignmentMode) === 'fixed'
            ? template?.fixedTransportIdSnapshot || linkedContract?.fixedTransportId || ''
            : '',
        transportNameSnapshot:
          (template?.transportAssignmentModeSnapshot || linkedContract?.transportAssignmentMode) === 'fixed'
            ? template?.fixedTransportNameSnapshot || linkedContract?.fixedTransportNameSnapshot || ''
            : '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const estimateSnapshot = buildEstimatedPricingSnapshot(basePayload, liquidationSettings);

      await addDoc(collection(db, resolveServicesCollectionPath(companyId)), {
        ...basePayload,
        salePrice: estimateSnapshot.estimatedTotalToCharge || 0,
        ...estimateSnapshot,
      });

      await updateDoc(doc(db, resolveServiceTemplatesCollectionPath(companyId), template.id), {
        lastGeneratedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setActiveTab('services');
      showMessage?.(`Generamos un servicio nuevo desde la plantilla de ${template.clientNameSnapshot || 'cliente'}.`, 'success');
    } catch (error) {
      console.error('Error generando servicio desde plantilla:', error);
      showMessage?.('No pudimos generar el servicio desde la plantilla.', 'error');
    } finally {
      setUpdatingServiceId('');
    }
  };

  const handleOpenTemplateEditor = (template = null) => {
    setTemplateModalState({ open: true, template });
  };

  const handleSaveClientContract = async (form, client, currentContract = null) => {
    if (!companyId || !client?.id) {
      showMessage?.('No encontramos la empresa o el cliente para guardar este acuerdo.', 'error');
      return;
    }

    const trimmedName = String(form?.name || '').trim();
    if (!trimmedName) {
      showMessage?.('Definí un nombre comercial para este acuerdo.', 'warning');
      return;
    }

    if (form.commercialModel === 'monthly_fixed' && Number(form?.monthlyFee || 0) <= 0) {
      showMessage?.('Cargá el valor mensual pactado para el abono fijo.', 'warning');
      return;
    }

    if (form.commercialModel !== 'monthly_fixed') {
      if (form.pricingRule === 'manual_total' && Number(form?.agreedPricePerTrip || 0) <= 0) {
        showMessage?.('Cargá un precio de referencia por viaje para este acuerdo.', 'warning');
        return;
      }
      if (form.pricingRule === 'fixed_margin' && Number(form?.fixedMarginAmount || 0) <= 0) {
        showMessage?.('Cargá la ganancia fija por viaje para este acuerdo.', 'warning');
        return;
      }
      if (form.pricingRule === 'percentage_margin' && Number(form?.marginPercent || 0) <= 0) {
        showMessage?.('Cargá el porcentaje de ganancia para este acuerdo.', 'warning');
        return;
      }
    }

    const contractId = currentContract?.id || `client-${client.id}`;
    const billingCadence = String(form?.billingCadence || resolveContractBillingCadence({ commercialModel: form?.commercialModel })).trim() || 'per_trip';
    const serviceDaysOfWeek = Array.isArray(form?.serviceDaysOfWeek)
      ? form.serviceDaysOfWeek.filter(Boolean)
      : [];
    const fixedTransportId = String(form?.fixedTransportId || '').trim();
    const suggestedTransportId = String(form?.suggestedTransportId || '').trim();
    const fixedDriverId = String(form?.fixedDriverId || '').trim();
    const suggestedDriverId = String(form?.suggestedDriverId || '').trim();
    setSavingContract(true);
    try {
      await setDoc(
        doc(db, resolveServiceContractDocPath(companyId, contractId)),
        {
          companyId,
          clientId: client.id,
          clientNameSnapshot: buildClientDisplayName(client),
          name: trimmedName,
          status: form.status || 'active',
          commercialModel: form.commercialModel || 'trip_rate',
          billingCadence,
          billingCadenceLabel: resolveBillingCadenceLabel(billingCadence),
          billingDayOfWeek: billingCadence === 'weekly' ? String(form?.billingDayOfWeek || 'friday').trim() : '',
          billingDayOfMonth: billingCadence === 'monthly' ? Math.max(1, Math.min(31, Number(form?.billingDayOfMonth || 1) || 1)) : 0,
          serviceDaysOfWeek,
          serviceTimeWindowStart: String(form?.serviceTimeWindowStart || '').trim(),
          serviceTimeWindowEnd: String(form?.serviceTimeWindowEnd || '').trim(),
          serviceStartDate: String(form?.serviceStartDate || '').trim(),
          serviceEndDate: String(form?.serviceEndDate || '').trim(),
          transportAssignmentMode: String(form?.transportAssignmentMode || 'free').trim() || 'free',
          fixedTransportId,
          fixedTransportNameSnapshot: fixedTransportId ? resolveTransportSnapshotName(fixedTransportId) : '',
          suggestedTransportId,
          suggestedTransportNameSnapshot: suggestedTransportId ? resolveTransportSnapshotName(suggestedTransportId) : '',
          driverAssignmentMode: String(form?.driverAssignmentMode || 'free').trim() || 'free',
          fixedDriverId,
          fixedDriverNameSnapshot: fixedDriverId ? resolveDriverSnapshotName(fixedDriverId) : '',
          suggestedDriverId,
          suggestedDriverNameSnapshot: suggestedDriverId ? resolveDriverSnapshotName(suggestedDriverId) : '',
          pricingRule: form.pricingRule || 'manual_total',
          billingContactName: String(form?.billingContactName || '').trim(),
          billingPhone: String(form?.billingPhone || '').trim(),
          billingEmail: String(form?.billingEmail || '').trim(),
          autoReminderEnabled: Boolean(form?.autoReminderEnabled),
          reminderDaysBefore: Math.max(0, Number(form?.reminderDaysBefore || 0)) || 0,
          monthlyFee: Number(form.monthlyFee || 0) || 0,
          agreedPricePerTrip: Number(form.agreedPricePerTrip || 0) || 0,
          fixedMarginAmount: Number(form.fixedMarginAmount || 0) || 0,
          marginPreset: String(form?.marginPreset || inferMarginPreset(form?.marginPercent)).trim() || 'normal',
          marginPercent: Number(form.marginPercent || 0) || 0,
          notes: String(form?.notes || '').trim(),
          updatedAt: serverTimestamp(),
          createdAt: currentContract?.createdAt || serverTimestamp(),
        },
        { merge: true }
      );

      setContractModalState({ open: false, client: null, contract: null });
      showMessage?.(`El acuerdo comercial de ${buildClientDisplayName(client)} quedó guardado.`, 'success');
    } catch (error) {
      console.error('Error guardando acuerdo comercial:', error);
      showMessage?.('No pudimos guardar el acuerdo comercial. Intentemos otra vez.', 'error');
    } finally {
      setSavingContract(false);
    }
  };

  const handleSendServiceToLogistics = async (service, options = {}) => {
    if (!companyId || !service?.id) {
      showMessage?.('No pudimos ubicar el servicio para pasarlo a logística.', 'error');
      return;
    }

    const normalizedStatus = normalizeServiceStatus(service.status);
    if (normalizedStatus !== 'pending') {
      showMessage?.('Este servicio ya salió de la etapa comercial inicial.', 'warning');
      return;
    }

    setUpdatingServiceId(service.id);
    try {
      await updateDoc(doc(db, resolveServiceDocPath(companyId, service.id)), {
        status: 'pending_logistics',
        statusLabel: 'Pendiente de logística',
        logisticsRequestedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      showMessage?.(
        `El servicio de ${service.clientNameSnapshot || 'este cliente'} ya quedó en cola para logística.`,
        'success'
      );
      if (options?.openLogistics && typeof onGoToLogistics === 'function') {
        onGoToLogistics();
      }
    } catch (error) {
      console.error('Error enviando servicio a logística:', error);
      showMessage?.('No pudimos pasarlo a logística. Intentemos otra vez.', 'error');
    } finally {
      setUpdatingServiceId('');
    }
  };

  const handleMarkClosurePaid = async (closure) => {
    if (!companyId || !closure?.id) {
      showMessage?.('No pudimos ubicar el cierre para marcarlo como pagado.', 'error');
      return;
    }

    setPayingClosureId(closure.id);
    try {
      const linkedServiceIds = Array.isArray(closure?.linkedServiceIds)
        ? closure.linkedServiceIds.filter(Boolean)
        : [];
      const linkedServices = services.filter((service) => linkedServiceIds.includes(service.id));
      const totalOperationalCost = linkedServices.reduce(
        (acc, service) => acc + Math.max(0, Number(service?.realizedOperationalCost || 0)),
        0
      );
      const closureSaleTotal = Number(closure?.saleTotalPending || 0) || 0;
      const closureMarginTotal = Number(closure?.marginTotalPending || 0) || 0;
      const recognizedAt = new Date();
      const batch = writeBatch(db);

      linkedServices.forEach((service, index) => {
        let recognizedSalePrice = Number(service?.realizedSalePrice || 0) || 0;
        let recognizedMargin = Number(service?.realizedMargin || 0) || 0;
        const isMonthlyFixed = String(service?.commercialModel || '').trim().toLowerCase() === 'monthly_fixed';

        if (isMonthlyFixed) {
          const allocationWeight =
            totalOperationalCost > 0
              ? (Number(service?.realizedOperationalCost || 0) || 0) / totalOperationalCost
              : 1 / Math.max(1, linkedServices.length);

          if (index === linkedServices.length - 1) {
            const distributedSale = linkedServices
              .slice(0, -1)
              .reduce((acc, item) => acc + Math.round((closureSaleTotal * (((Number(item?.realizedOperationalCost || 0) || 0) / Math.max(totalOperationalCost, 1)) || 0)) * 100) / 100, 0);
            const distributedMargin = linkedServices
              .slice(0, -1)
              .reduce((acc, item) => acc + Math.round((closureMarginTotal * (((Number(item?.realizedOperationalCost || 0) || 0) / Math.max(totalOperationalCost, 1)) || 0)) * 100) / 100, 0);
            recognizedSalePrice = Math.max(0, closureSaleTotal - distributedSale);
            recognizedMargin = closureMarginTotal - distributedMargin;
          } else {
            recognizedSalePrice = Math.round(closureSaleTotal * allocationWeight * 100) / 100;
            recognizedMargin = Math.round(closureMarginTotal * allocationWeight * 100) / 100;
          }
        }

        batch.update(doc(db, resolveServiceDocPath(companyId, service.id)), {
          recognitionPending: false,
          recognizedInBalance: true,
          revenueRecognizedAt: recognizedAt,
          recognizedAt,
          recognizedSalePrice,
          recognizedMargin,
          updatedAt: serverTimestamp(),
        });
      });

      batch.update(doc(db, resolveServiceClosuresCollectionPath(companyId), closure.id), {
        status: 'paid',
        statusLabel: 'Pagado',
        paidAt: recognizedAt,
        recognizedAt,
        updatedAt: serverTimestamp(),
      });

      await batch.commit();
      showMessage?.(`El cierre de ${closure.clientNameSnapshot || 'cliente'} quedó marcado como pagado.`, 'success');
    } catch (error) {
      console.error('Error marcando pago realizado en cierre acumulado:', error);
      showMessage?.('No pudimos marcar este cierre como pagado. Intentemos otra vez.', 'error');
    } finally {
      setPayingClosureId('');
    }
  };

  return (
    <div className="min-h-full bg-slate-100 p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <section
          id="service-hero"
          className="overflow-hidden rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,#0f172a_0%,#155e75_56%,#cffafe_100%)] px-5 py-5 text-white shadow-[0_24px_56px_rgba(15,23,42,0.18)]"
        >
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-100">
                  Servicio
                </span>
                <span className="rounded-full border border-cyan-100/20 bg-slate-950/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-50">
                  Empresa activa: {companyName || companyId || 'Sin empresa'}
                </span>
              </div>
              <h1 className="mt-3 text-2xl font-black tracking-tight md:text-3xl">Ventas de servicios y paso a logística</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-cyan-50/90">
                Desde acá armamos el servicio, definimos cliente, recorrido y acuerdo comercial, y después lo pasamos a logística para ejecutarlo.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {onStartTutorial ? (
                <button
                  type="button"
                  onClick={onStartTutorial}
                  className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                    tutorialSeen
                      ? 'border-cyan-200/60 bg-cyan-50/12 text-cyan-50 hover:bg-cyan-50/18'
                      : 'border-cyan-300 bg-cyan-50 px-4 py-3 text-cyan-900 shadow-[0_0_0_6px_rgba(34,211,238,0.16),0_0_24px_rgba(34,211,238,0.24)] animate-pulse hover:bg-cyan-100'
                  }`}
                >
                  <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="h-4 w-4" />
                  Tutorial guiado
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setCreateModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl border border-white/30 bg-white px-5 py-3 text-sm font-black text-cyan-950 shadow-[0_16px_32px_rgba(255,255,255,0.2)] transition hover:-translate-y-0.5 hover:bg-cyan-50"
              >
                <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                Nuevo servicio
              </button>
              <button
                type="button"
                onClick={onBackToMenu}
                className="inline-flex items-center gap-2 rounded-2xl border border-cyan-100/25 bg-slate-950/20 px-4 py-3 text-sm font-semibold text-cyan-50 transition hover:bg-slate-950/28"
              >
                <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="h-4 w-4" />
                Volver al inicio
              </button>
            </div>
          </div>
        </section>

        <section id="service-summary" className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          <SummaryCard
            title="Clientes listos"
            value={clients.length}
            helper="Fichas reutilizables para pedir servicio sin volver a cargar datos."
            tone="cyan"
          />
          <SummaryCard
            title="Pendientes"
            value={serviceSummary.pendingCount + serviceSummary.logisticsPendingCount + serviceSummary.assignedCount}
            helper={`${serviceSummary.logisticsPendingCount} listos para logística · ${serviceSummary.completedCount} realizados`}
            tone="amber"
          />
          <SummaryCard
            title="Facturado"
            value={formatCurrency(serviceSummary.billedRevenue)}
            helper={`${serviceSummary.billedCount} servicios cerrados con lectura comercial`}
            tone="emerald"
          />
          <SummaryCard
            title="Contratos activos"
            value={contractSummary.activeCount}
            helper={`${formatCurrency(contractSummary.monthlyRevenue)} mensuales pactados`}
            tone="violet"
          />
        </section>

        <section id="service-tabs" className="rounded-[32px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-[0_20px_45px_rgba(15,23,42,0.06)]">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {TAB_OPTIONS.map((tab) => {
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`group relative inline-flex min-h-[104px] items-center gap-3 overflow-hidden rounded-[24px] border px-4 py-4 text-left transition ${
                    active
                      ? 'border-cyan-300 bg-[linear-gradient(135deg,#ecfeff_0%,#ffffff_85%)] text-cyan-900 shadow-[0_16px_32px_rgba(8,145,178,0.14)]'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:shadow-[0_12px_24px_rgba(15,23,42,0.06)]'
                  }`}
                >
                  <span className={`absolute inset-x-0 top-0 h-1.5 ${active ? 'bg-cyan-500' : 'bg-transparent group-hover:bg-slate-200'}`} />
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl shadow-sm ${active ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-50 text-slate-500 group-hover:bg-white group-hover:text-slate-700'}`}>
                    <Icon path={tab.iconPath} className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] opacity-60">Sección</span>
                    <span className="mt-1 block text-[15px] font-black leading-5 tracking-tight">{tab.label}</span>
                    <span className="mt-1 block text-xs leading-5 opacity-75">{tab.caption}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section id="service-panel" className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_20px_45px_rgba(15,23,42,0.06)]">
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">Panel activo</p>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">{panelTitle}</h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                {TAB_OPTIONS.find((tab) => tab.key === activeTab)?.caption}
              </p>
            </div>
            {activeTab === 'services' ? (
              <div className="flex flex-wrap gap-2">
                {[
                  `${serviceSummary.activeCount} activos`,
                  `${serviceSummary.logisticsPendingCount} para logística`,
                  `${serviceCollections.review.length} revisión`,
                  `${serviceSummary.archivedCount} archivados`,
                ].map((item) => (
                  <span key={item} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                    {item}
                  </span>
                ))}
              </div>
            ) : null}
            {activeTab === 'scheduled' && templates.length > 0 ? (
              <button
                type="button"
                onClick={() => handleOpenTemplateEditor(null)}
                className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700"
              >
                <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                Nueva plantilla
              </button>
            ) : null}
          </div>

          <div className="mt-6">
            {activeTab === 'services' ? (
              services.length > 0 ? (
                <div className="space-y-4">
                  <div
                    id="service-active-filters"
                    className="flex flex-wrap items-end justify-between gap-3 rounded-[26px] border border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#ffffff_78%)] px-4 py-4 shadow-sm"
                  >
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Vista de servicios</p>
                      <p className="mt-1 text-sm text-slate-600">
                        Mostramos arriba lo que sigue vivo en operación. Lo ya realizado queda ordenado en archivados y en el historial del cliente.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
                        {[
                          { key: 'active', label: `Activos (${serviceCollections.active.length})` },
                          { key: 'review', label: `Revisión (${serviceCollections.review.length})` },
                          { key: 'archived', label: `Archivados (${serviceCollections.archived.length})` },
                        ].map((option) => {
                          const selected = serviceListMode === option.key;
                          return (
                            <button
                              key={option.key}
                              type="button"
                              onClick={() => setServiceListMode(option.key)}
                              className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                                selected
                                  ? 'bg-slate-950 text-white shadow-sm'
                                  : 'text-slate-600 hover:bg-slate-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                      {serviceListMode === 'archived' ? (
                        <label className="flex min-w-[220px] flex-col gap-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Filtrar por cliente
                          <select
                            value={archivedClientFilter}
                            onChange={(event) => setArchivedClientFilter(event.target.value)}
                            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
                          >
                            <option value="all">Todos los clientes</option>
                            {archivedClientOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                    </div>
                  </div>

                  {(serviceListMode === 'active'
                    ? serviceCollections.active
                    : serviceListMode === 'review'
                    ? serviceCollections.review
                    : filteredArchivedServices).length > 0 ? (
                    (serviceListMode === 'active'
                      ? serviceCollections.active
                      : serviceListMode === 'review'
                      ? serviceCollections.review
                      : filteredArchivedServices)
                    .slice(0, 12)
                    .map((service) => {
                      const statusMeta = getServiceStatusMeta(service.status);
                      const normalizedStatus = normalizeServiceStatus(service.status);
                      const canSendToLogistics = normalizedStatus === 'pending';
                      const isUpdating = updatingServiceId === service.id;
                      const recognizedSalePrice = Number(service?.recognizedSalePrice ?? 0) || 0;
                      const recognizedMargin = Number(service?.recognizedMargin ?? 0) || 0;
                      const realizedSalePrice = Number(service?.realizedSalePrice ?? 0) || 0;
                      const realizedOperationalCost = Number(service?.realizedOperationalCost ?? 0) || 0;
                      const realizedMargin = Number(service?.realizedMargin ?? 0) || 0;
                      const estimatedSalePrice = Number(service?.salePrice ?? 0) || 0;
                      const estimatedOperationalCost = Number(service?.estimatedOperationalCost ?? 0) || 0;
                      const estimatedMargin = Number(service?.estimatedMargin ?? 0) || 0;
                      const recognitionBadge = resolveServiceRecognitionBadge(service);
                      const hasRealizedFinancials =
                        recognizedSalePrice > 0 ||
                        realizedSalePrice > 0 ||
                        realizedOperationalCost > 0 ||
                        recognizedMargin !== 0 ||
                        realizedMargin !== 0;
                      const displaySalePrice = hasRealizedFinancials ? (recognizedSalePrice || realizedSalePrice) : estimatedSalePrice;
                      const displayOperationalCost = hasRealizedFinancials ? realizedOperationalCost : estimatedOperationalCost;
                      const displayMargin = hasRealizedFinancials ? (recognizedMargin || realizedMargin) : estimatedMargin;
                      const displayDate =
                        service?.financialClosedAt ||
                        service?.revenueRecognizedAt ||
                        service?.completedAt ||
                        service?.paidAt ||
                        service?.invoicedAt ||
                        service?.scheduledDate ||
                        service?.date ||
                        service?.createdAt;
                      const financialPrefix = hasRealizedFinancials ? 'real.' : 'est.';

                      return (
                      <div key={service.id} className={`overflow-hidden rounded-[30px] border px-5 py-5 shadow-[0_18px_42px_rgba(15,23,42,0.08)] transition-all duration-200 ${getServiceStatusSurface(service.status)}`}>
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded-full border border-slate-200 bg-white/95 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-600 shadow-sm">
                                {service.serviceModeLabel || service.serviceMode || 'Servicio'}
                              </span>
                              <span className="rounded-full border border-cyan-200 bg-cyan-50/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-700 shadow-sm">
                                {service.serviceTransportLabel || service.serviceTypeLabel || service.serviceTransport || service.serviceType || 'Transporte'}
                              </span>
                              <span className="rounded-full border border-violet-200 bg-violet-50/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-violet-700 shadow-sm">
                                {service.serviceTaskLabel || service.serviceTask || 'Tarea'}
                              </span>
                              <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusMeta.tone}`}>
                                {statusMeta.label}
                              </span>
                              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${recognitionBadge.tone}`}>
                                {recognitionBadge.label}
                              </span>
                              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] shadow-sm ${
                                hasRealizedFinancials
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                  : 'border-amber-200 bg-amber-50 text-amber-700'
                              }`}>
                                {hasRealizedFinancials ? 'Cierre real' : 'Lectura estimada'}
                              </span>
                            </div>
                            <p className="mt-4 text-xl font-black tracking-tight text-slate-950">
                              {service.clientNameSnapshot || service.clientName || 'Cliente sin nombre'}
                            </p>
                            <p className="mt-2 text-sm font-semibold text-slate-700">
                              {service.origin || 'Sin origen'} <span className="mx-1 text-slate-400">→</span> {service.destination || 'Sin destino'}
                            </p>
                            {(service.routeNameSnapshot || service.driverNameSnapshot) ? (
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                {service.routeNameSnapshot ? (
                                  <span className="rounded-full border border-slate-200 bg-white/90 px-3 py-1 text-[11px] font-semibold text-slate-600 shadow-sm">
                                    Ruta: {service.routeNameSnapshot}
                                  </span>
                                ) : null}
                                {service.driverNameSnapshot ? (
                                  <span className="rounded-full border border-slate-200 bg-white/90 px-3 py-1 text-[11px] font-semibold text-slate-600 shadow-sm">
                                    Chofer: {service.driverNameSnapshot}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                            {service.notes ? (
                              <div className="mt-3 max-w-3xl rounded-2xl border border-slate-200/80 bg-white/70 px-3 py-2 shadow-sm">
                                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Observación</p>
                                <p className="mt-1 text-xs leading-5 text-slate-600">
                                {service.notes}
                                </p>
                              </div>
                            ) : null}
                          </div>
                          <div className="min-w-[220px] rounded-[24px] border border-white/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(248,250,252,0.94)_100%)] px-4 py-4 text-right shadow-[0_14px_30px_rgba(15,23,42,0.08)] backdrop-blur-sm">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Monto comercial</p>
                              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${
                                hasRealizedFinancials
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                  : 'border-slate-200 bg-slate-100 text-slate-600'
                              }`}>
                                {hasRealizedFinancials ? 'Real' : 'Estimado'}
                              </span>
                            </div>
                            <p className="mt-2 text-3xl font-black tracking-tight text-slate-950">{formatCurrency(displaySalePrice)}</p>
                            {displayOperationalCost > 0 ? (
                              <div className="mt-3 rounded-2xl border border-slate-200/90 bg-white/80 px-3 py-3 text-left shadow-inner">
                                <div className="flex items-center justify-between gap-3 text-[11px]">
                                  <span className="font-semibold uppercase tracking-[0.16em] text-slate-500">Liquidación {financialPrefix}</span>
                                  <span className="font-black text-slate-800">{formatCurrency(displayOperationalCost)}</span>
                                </div>
                                <div className="mt-2 flex items-center justify-between gap-3 text-[11px]">
                                  <span className="font-semibold uppercase tracking-[0.16em] text-slate-500">Diferencia {financialPrefix}</span>
                                  <span className="font-black text-emerald-700">{formatCurrency(displayMargin)}</span>
                                </div>
                              </div>
                            ) : null}
                            <p className="mt-3 text-xs font-medium text-slate-500">{formatShortDateTime(displayDate)}</p>
                          </div>
                        </div>
                        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200/80 pt-4">
                          <p className="text-xs font-medium leading-5 text-slate-600">
                            {serviceListMode === 'review'
                              ? 'Este servicio quedó tomado por logística, pero no terminó de cerrar limpio. Lo separamos para revisarlo sin ruido.'
                              : normalizedStatus === 'pending_logistics'
                              ? 'Ya está en la cola de logística para que después lo levantemos desde la operación.'
                              : normalizedStatus === 'pending'
                              ? 'Todavía está en etapa comercial. Cuando esté listo, lo mandamos a logística.'
                              : normalizedStatus === 'assigned'
                              ? 'Ya lo tomó una ruta operativa. Acá te mostramos la ruta y el chofer para seguirle el hilo.'
                              : 'Este servicio ya avanzó a una etapa posterior.'}
                          </p>
                          {canSendToLogistics ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleSendServiceToLogistics(service)}
                                disabled={isUpdating}
                                className="inline-flex items-center gap-2 rounded-2xl border border-violet-300 bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <Icon path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" className="h-4 w-4" />
                                {isUpdating ? 'Enviando...' : 'Enviar a logística'}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSendServiceToLogistics(service, { openLogistics: true })}
                                disabled={isUpdating}
                                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/90 px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-cyan-200 hover:bg-cyan-50 hover:text-cyan-800 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <Icon path="M15 10.5l4.5 4.5m0 0L15 19.5m4.5-4.5H9a4.5 4.5 0 010-9h1.5" className="h-4 w-4" />
                                {isUpdating ? 'Preparando...' : 'Enviar y abrir logística'}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )})
                  ) : (
                    <EmptyState
                      title={
                        serviceListMode === 'active'
                          ? 'No hay servicios activos en este momento'
                          : serviceListMode === 'review'
                          ? 'No hay servicios para revisar'
                          : 'No hay archivados para este filtro'
                      }
                      description={
                        serviceListMode === 'active'
                          ? 'Cuando generes pedidos nuevos o queden en tránsito, los vamos a ver acá para trabajarlos sin ruido histórico.'
                          : serviceListMode === 'review'
                          ? 'Si algo queda tomado por logística pero no termina de cerrar bien, lo vas a ver acá para revisarlo con calma.'
                          : archivedClientFilter === 'all'
                          ? 'Los servicios ya realizados van a quedar disponibles acá y también dentro del historial de cada cliente.'
                          : 'Probá con otro cliente o volvé a “Todos los clientes” para ver más archivados.'
                      }
                    />
                  )}
                </div>
              ) : (
                <EmptyState
                  id="service-active-filters"
                  title="Todavía no hay servicios cargados"
                  description="Todavía no cargaste el primer servicio. Desde acá nace el pedido comercial, después lo dejamos pendiente de logística y recién ahí lo levantamos para ejecutarlo."
                  action={(
                    <button
                      type="button"
                      onClick={() => setCreateModalOpen(true)}
                      className="inline-flex items-center gap-2 rounded-2xl border border-violet-300 bg-violet-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700"
                    >
                      <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                      Cargar primer servicio
                    </button>
                  )}
                  footer={(
                    <div className="rounded-[22px] border border-slate-200 bg-white px-4 py-4 text-left shadow-sm">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Clientes listos para usar</p>
                      {sortedClients.length > 0 ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {sortedClients.slice(0, 6).map((client) => (
                            <span key={client.id} className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-800">
                              {buildClientDisplayName(client)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-slate-500">
                          Todavía no hay clientes disponibles. Primero necesitamos una ficha de cliente para poder vender un servicio.
                        </p>
                      )}
                    </div>
                  )}
                />
              )
            ) : null}

            {activeTab === 'scheduled' ? (
              templates.length > 0 ? (
                <div className="space-y-4">
                  {templates.slice(0, 8).map((template) => (
                    <div key={template.id} className="rounded-[28px] border border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#ffffff_70%)] px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.06)]">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-700">
                              Programado
                            </span>
                            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                              {template.frequencyLabel || template.frequency || 'Frecuencia manual'}
                            </span>
                            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                              {template.serviceTransportLabel || template.serviceTypeLabel || 'Camioneta'}
                            </span>
                            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                              {template.serviceTaskLabel || template.serviceTask || 'Retiro y entrega'}
                            </span>
                          </div>
                          <p className="mt-3 text-lg font-black tracking-tight text-slate-950">{template.clientNameSnapshot || template.clientName || 'Cliente sin nombre'}</p>
                          <p className="mt-2 text-sm text-slate-600">
                            {template.frequencyLabel || template.frequency || 'Frecuencia manual'} · {template.destination || 'Sin destino'}
                          </p>
                        </div>
                        <div className="rounded-[22px] border border-slate-200 bg-white px-4 py-3 text-right shadow-sm">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Lectura comercial</p>
                          <p className="mt-1 text-sm font-black tracking-tight text-slate-950">Se calcula al consolidar</p>
                          <p className="mt-1 text-xs text-slate-500">Costo + ganancia sugerida al cerrar el recorrido</p>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
                        <p className="text-xs text-slate-500">
                          {template.lastGeneratedAt ? `Última generación ${formatShortDateTime(template.lastGeneratedAt)}` : 'Todavía no generaste un servicio desde esta plantilla.'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenTemplateEditor(template)}
                            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                          >
                            <Icon path="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.93z" className="h-4 w-4" />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleGenerateServiceFromTemplate(template)}
                            disabled={updatingServiceId === template.id}
                            className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Icon path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" className="h-4 w-4" />
                            {updatingServiceId === template.id ? 'Generando...' : 'Generar servicio'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="Programados todavía vacíos"
                  description="Acá van a vivir los servicios recurrentes: semanal, mensual o por día fijo. La idea es generarlos con un clic sin volver a cargar cliente, chofer y precio."
                  action={(
                    <button
                      type="button"
                      onClick={() => handleOpenTemplateEditor(null)}
                      className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700"
                    >
                      <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                      Crear primera plantilla
                    </button>
                  )}
                />
              )
            ) : null}

            {activeTab === 'clients' ? (
              <ServiceClientCommercialView
                clients={sortedClients}
                contracts={contracts}
                closures={closures}
                services={services}
                onEditClient={handleOpenClientContractEditor}
              />
            ) : null}

            {activeTab === 'contracts' ? (
              contracts.length > 0 ? (
                <div className="space-y-5">
                  <ServiceContractModesView />
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {contracts.slice(0, 8).map((contract) => (
                      <div key={contract.id} className="relative overflow-hidden rounded-[30px] border border-violet-200 bg-[linear-gradient(135deg,#ede9fe_0%,#ffffff_45%,#faf5ff_100%)] px-5 py-5 shadow-[0_20px_44px_rgba(76,29,149,0.12)]">
                        <div className="absolute inset-x-0 top-0 h-1.5 bg-[linear-gradient(90deg,#7c3aed_0%,#22d3ee_100%)]" />
                        <div className="absolute -right-8 top-0 h-24 w-24 rounded-full bg-violet-200/50 blur-3xl" />
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded-full border border-violet-200 bg-white/95 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-violet-700 shadow-sm">
                                {contract.commercialModelLabel || contract.contractType || 'Contrato'}
                              </span>
                              <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                                {String(contract.status || 'draft').replace(/_/g, ' ')}
                              </span>
                            </div>
                            <p className="mt-3 text-xl font-black tracking-tight text-slate-950">{contract.clientNameSnapshot || contract.clientName || 'Cliente sin nombre'}</p>
                            <p className="mt-1 text-sm font-medium text-slate-600">{contract.name || 'Sin nombre comercial'}</p>
                            <div className="mt-4 grid gap-3 md:grid-cols-2">
                              <div className="rounded-[22px] border border-white/80 bg-white/90 px-4 py-3 shadow-sm">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Modo</p>
                                <p className="mt-1 text-sm font-black text-slate-900">{contract.commercialModelLabel || contract.contractType || 'Contrato'}</p>
                                <p className="mt-1 text-xs text-slate-500">{contract.pricingRuleLabel || 'Regla comercial configurada'}</p>
                              </div>
                              <div className="rounded-[22px] border border-white/80 bg-white/90 px-4 py-3 shadow-sm">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Cobro</p>
                                <p className="mt-1 text-sm font-black text-slate-900">{contract.billingCadenceLabel || resolveBillingCadenceLabel(contract.billingCadence)}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {contract.billingCadence === 'weekly'
                                    ? `Cada ${contract.billingDayOfWeek || 'semana'}`
                                    : contract.billingCadence === 'monthly'
                                    ? `Día ${contract.billingDayOfMonth || '-'}`
                                    : 'Reconoce al cierre de cada viaje'}
                                </p>
                              </div>
                              <div className="rounded-[22px] border border-white/80 bg-white/90 px-4 py-3 shadow-sm">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Contacto de cobro</p>
                                <p className="mt-1 text-sm font-black text-slate-900">{contract.billingContactName || 'Sin contacto de cobro'}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {[contract.billingPhone, contract.billingEmail].filter(Boolean).join(' · ') || 'Todavía sin datos de contacto'}
                                </p>
                              </div>
                              <div className="rounded-[22px] border border-white/80 bg-white/90 px-4 py-3 shadow-sm">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Calendario</p>
                                <p className="mt-1 text-sm font-black text-slate-900">
                                  {Array.isArray(contract?.serviceDaysOfWeek) && contract.serviceDaysOfWeek.length > 0
                                    ? `${contract.serviceDaysOfWeek.length} días`
                                    : 'Libre / a pedido'}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {[contract?.serviceTimeWindowStart, contract?.serviceTimeWindowEnd].filter(Boolean).join(' a ') || 'Sin franja horaria'}
                                </p>
                              </div>
                              <div className="rounded-[22px] border border-white/80 bg-white/90 px-4 py-3 shadow-sm">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Asignación</p>
                                <p className="mt-1 text-sm font-black text-slate-900">
                                  {contract.transportAssignmentMode === 'fixed'
                                    ? contract.fixedTransportNameSnapshot || 'Fija'
                                    : contract.transportAssignmentMode === 'suggested'
                                    ? contract.suggestedTransportNameSnapshot || 'Sugerida'
                                    : 'Libre'}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {contract.transportAssignmentMode === 'fixed'
                                    ? 'La unidad nace preasignada'
                                    : contract.transportAssignmentMode === 'suggested'
                                    ? 'Se propone una unidad inicial'
                                    : 'Logística define la unidad'}
                                </p>
                              </div>
                              <div className="rounded-[22px] border border-white/80 bg-white/90 px-4 py-3 shadow-sm">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Chofer</p>
                                <p className="mt-1 text-sm font-black text-slate-900">
                                  {contract.driverAssignmentMode === 'fixed'
                                    ? contract.fixedDriverNameSnapshot || 'Fijo'
                                    : contract.driverAssignmentMode === 'suggested'
                                    ? contract.suggestedDriverNameSnapshot || 'Sugerido'
                                    : 'Libre'}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {contract.driverAssignmentMode === 'fixed'
                                    ? 'El servicio solo aparece para ese chofer'
                                    : contract.driverAssignmentMode === 'suggested'
                                    ? 'Se propone un chofer inicial'
                                    : 'Cualquier chofer disponible puede tomarlo'}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="min-w-[190px] rounded-[24px] border border-white/90 bg-white/90 px-4 py-4 text-right shadow-[0_14px_30px_rgba(124,58,237,0.10)] backdrop-blur-sm">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-500">Referencia</p>
                            <p className="mt-1 text-2xl font-black tracking-tight text-slate-950">{formatCurrency(contract.monthlyFee || contract.agreedPricePerTrip || 0)}</p>
                            <p className="mt-2 text-xs text-slate-500">
                              {contract.monthlyFee ? 'Valor mensual pactado' : 'Valor base del acuerdo'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <ServiceContractModesView />
                  <EmptyState
                    title="Todavía no hay contratos activos"
                    description="Acá vamos a concentrar abonos mensuales, tarifas por viaje y acuerdos comerciales con clientes recurrentes para que después impacten directo en balance."
                  />
                </div>
              )
            ) : null}

            {activeTab === 'closures' ? (
              <ServiceClosuresView
                closures={closures}
                clients={sortedClients}
                services={services}
                payingClosureId={payingClosureId}
                onMarkPaid={handleMarkClosurePaid}
              />
            ) : null}

            {activeTab === 'calendar' ? (
              <ServiceCalendarView
                clients={sortedClients}
                contracts={contracts}
                templates={templates}
                transports={transports}
                drivers={drivers}
                activatingTemplateId={updatingServiceId}
                onActivateTemplate={handleGenerateServiceFromTemplate}
              />
            ) : null}

            {activeTab === 'market' ? <ServiceMarketView /> : null}
          </div>
        </section>
      </div>
      <ServiceCreateModal
        open={createModalOpen}
        clients={sortedClients}
        locations={locations}
        defaultOperationalOrigin={defaultOperationalOrigin}
        contractsByClientId={contractsByClientId}
        liquidationSettings={liquidationSettings}
        saving={savingService}
        onCreateClient={handleCreateServiceClient}
        onOpenClientContractEditor={handleOpenClientContractEditor}
        onClose={() => {
          if (savingService) return;
          setCreateModalOpen(false);
        }}
        onSubmit={handleCreateService}
      />
      <ServiceTemplateModal
        open={templateModalState.open}
        initialTemplate={templateModalState.template}
        clients={sortedClients}
        locations={locations}
        saving={savingTemplate}
        onClose={() => {
          if (savingTemplate) return;
          setTemplateModalState({ open: false, template: null });
        }}
        onSubmit={handleCreateTemplate}
      />
      <ServiceContractEditorModal
        open={contractModalState.open}
        client={contractModalState.client}
        contract={contractModalState.contract}
        transports={transports}
        drivers={drivers}
        saving={savingContract}
        onClose={() => {
          if (savingContract) return;
          setContractModalState({ open: false, client: null, contract: null });
        }}
        onSubmit={handleSaveClientContract}
      />
    </div>
  );
};

export default ServiceApp;
